import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type RefObject,
} from 'react';
import { useSearchParams } from 'react-router-dom';
// The one calendar-date check in the app, shared with the catalog forms: shape
// AND existence, so '2026-02-30' is caught here rather than as a 422 naming the
// query parameter. Never `new Date(value)` — see `readState`.
import { isRealDate } from '@/components/catalog/form-utils';
import { ProjectFormModal } from '@/components/projects/ProjectFormModal';
import { ProjectPricingDialog } from '@/components/projects/ProjectPricingDialog';
import {
  ProjectCategoryCapacityCell,
  ProjectCitiesCell,
  ProjectCycleCell,
  ProjectDescriptionCell,
  ProjectEndDateCell,
  ProjectIntakeCell,
  ProjectNameCell,
  ProjectProductCell,
  ProjectRegionCell,
  ProjectStartDateCell,
  ProjectStatusCell,
  ProjectTotalCapacityCell,
  type ProjectEditContext,
} from '@/components/projects/ProjectInlineCells';
import {
  PROJECT_DRAG_HANDLE_SELECTOR,
  ProjectOrderControls,
} from '@/components/projects/ProjectOrderControls';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { preferencesStore } from '@/lib/preferences-store';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
// The SAME "which cycle is running today" rule that names a new deal or quote.
// Reused rather than reimplemented — see `applyDefaultCycle` below.
import { currentCycle } from '@/lib/document-naming';
import { formatDateTime, pluralise } from '@/lib/format';
import { denialReason, type ModuleSlug } from '@/lib/permissions';
import type { ListOrder, Paginated, Project, ProjectListParams } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  useColumnLayout,
  useFrozenOffsets,
  type ColumnLayoutController,
} from '@/lib/use-column-layout';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useIntakesByCycle, useProjectWriteOptions } from '@/lib/use-project-edit-options';
// One shared "Bangalore, India" formatter. Its only use left in this file is
// the selected City filter's label (in `rowLabels`) — the Cities COLUMN now
// renders through `CityChips` in `ProjectInlineCells`, not this import.
import { cityLabel } from '@/lib/use-project-options';
import { useProjectOrder, type ProjectOrdering } from '@/lib/use-project-order';
import { useReferenceOptions } from '@/lib/use-reference-options';
import {
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  Tooltip,
  TR,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * ── /projects — the list ────────────────────────────────────────────────────
 *
 * A project is ONE saleable run: the PRODUCTS it sells, the cycle (and
 * optionally the intake) it runs in, a capacity, its SEED regions of focus, and
 * the CITIES it runs in. Deals, quotes, invoices and deliverables are raised
 * *against* it. `docs/api/projects.md` in `emerge-backend-app` is the contract.
 *
 * ⚠️ **A project sells SEVERAL products (migration 156, deployed 2026-09-09):
 * read `products`, write `productIds` — plus `productId`, which is still the
 * PRIMARY and still `NOT NULL`.** This paragraph said "ONE saleable run of a
 * product"; a festival project could not carry its travel line, because the
 * travel package is a different product and the line guard refused it.
 *
 * ⚠️ **The deprecated singular `product` is STILL SERVED and must not be read
 * here.** It is the expand half of an expand/contract: the backend withdraws it
 * once every reader has moved and said so. Migration 095 renamed
 * `region` → `regions` without that overlap and left a blank Region column on
 * every project for hours — the WRITE failed loudly, the READ failed silently,
 * and the silent half is the one nobody runs first.
 *
 * ⚠️ **A project associates CITIES, not countries.** `countries` / `countryIds`
 * and `?countryId=` were removed from the contract on 2026-08-26 — a city
 * already carries its own country (`city.country`), which is why every city
 * is rendered with it. **Do not reintroduce a country association**, and never
 * derive a project's region from a country: `Country.regionId` is unrelated
 * upstream geography and returns Egypt as "South East Asia".
 *
 * The cost was taken knowingly by the owner: a project can no longer say "we
 * cover Kenya" without naming a Kenyan city. **Do not reintroduce a country
 * association to work around that** — and never derive a project's regions from
 * a country, because `Country.regionId` is upstream geography on an unrelated
 * id sequence and returns Egypt as "South East Asia".
 *
 * ⚠️ **A project's regions are a SET (migration 095, deployed 2026-08-29):
 * read `regions`, write `regionIds`, `[]` meaning unclassified.** The singular
 * `region` / `regionId` this file was written against are both GONE. The list
 * FILTER is the exception and is untouched — see the Region of focus control.
 *
 * Four rules from that contract shape this file, and none of them is stylistic:
 *
 *  1. **The list query is `.strict()`.** A key the schema does not know is a
 *     422 naming it — never a silently unfiltered list. So the request is built
 *     key by key in `requestParams()` from a closed set, and this page's own
 *     URL state is never spread into it.
 *  2. **An EMPTY value is a 422, not "no filter".** `?productId=` coerces to 0
 *     and fails `.positive()`. Every id read out of the URL is validated as a
 *     positive integer first, and `listQuery()` in `api.ts` drops empty keys —
 *     which is why nothing here hand-builds a query string.
 *  3. **`perPage` is 1..100 and the ENDPOINT defaults to 20**, lower than
 *     several other lists in this app. 101 is a 422, so the control offers
 *     20/50/100 and a URL value is clamped rather than forwarded. ⚠️ **This
 *     PAGE defaults to 100 while a cycle filter is set** so the cycle can be
 *     ordered end to end — see `CYCLE_PER_PAGE`.
 *  4. **`meta.totalPages` is 0, not 1, for an empty set.** Taken literally that
 *     disables both pager buttons and prints "Page 1 of 0".
 *
 * ⚠️ **The list's default order is MANUAL** — `?sort=manual`, the hand-ordered
 * `(sort_order, id)` sequence written by `PATCH /projects/{id}/position`, and
 * the ONLY order this page lets anybody rearrange. Every move is sent as an
 * ANCHOR (`{beforeId}` / `{afterId}`) naming a row that is **on screen**,
 * because `sort_order` is one global sequence and this page shows a filtered,
 * paged slice of it that the server cannot see. `use-project-order.ts` is the
 * model; the drag handlers and the two Move buttons are the two input paths,
 * and neither is a fallback for the other — **native HTML5 drag does not work
 * on touch at all.**
 *
 * **The Cycle filter starts on the cycle running today**, seeded into the URL
 * once on a bare arrival so it stays clearable and shareable like every other
 * filter. See `defaultCycleSettled` below for the mechanism and the trap it
 * avoids.
 *
 * ⚠️ **Any project count quoted anywhere in this file is a snapshot, not a
 * premise — re-measure before relying on one.** An empty page here is a
 * PERMANENT, ordinary state: staff create projects as they sell, and the Cycle
 * filter can legitimately hide all of them while others exist in different
 * cycles. A near-empty list also cannot be verified by looking at it — a
 * healthy handful of rows and a dead endpoint's empty state are the same
 * screen (`/deliverables` 500'd for a day behind exactly that) — so confirm
 * this page from the response, status and body, never from what it draws.
 */

/**
 * `projects` is a real permission module (backend migration 048): read /
 * create / update / delete, read granted to every seeded role, create/update
 * to the write tiers matching `requireRole(...WRITE_ROLES)`. DELETE is
 * `ADMIN_ROLES`, which `ability.canDelete` already knows, so nothing here
 * branches on the role itself. Named once so the gating call sites agree.
 */
const PROJECTS: ModuleSlug = 'projects';

/** The endpoint's own default. 1..100 — 101 is a 422, not a clamp. */
const DEFAULT_PER_PAGE = 20;
const MAX_PER_PAGE = 100;
const PER_PAGE_CHOICES = [20, 50, 100] as const;

/**
 * ⚠️ **A cycle is shown WHOLE, so it can be ordered end to end** — owner's
 * ruling, 2026-08-30. Filtering to one cycle raises the page size to the
 * endpoint's ceiling, because a hand-ordered list you can only see 20 rows of
 * is one you can only order 20 rows of.
 *
 * It is expressed as a different DEFAULT rather than as a forced value, so the
 * page-size control still works: pick 20 and it is written to the URL (20 ≠ the
 * default of 100 while a cycle is set) and honoured. Clearing the cycle takes
 * the default back to 20. Same mechanism, and the same reason, as writing the
 * default cycle INTO the URL rather than applying it behind one — a default you
 * cannot override is not a default, it is a rule with no sentence explaining it.
 *
 * ⚠️ **100 is the endpoint's hard ceiling and 101 is a 422**, so a cycle with
 * more than 100 projects genuinely cannot be ordered end to end. That is said
 * on screen beside the rows rather than left to look complete — see the
 * ordering notice in the page.
 */
const CYCLE_PER_PAGE = MAX_PER_PAGE;

function defaultPerPage(cycleId: string): number {
  return cycleId ? CYCLE_PER_PAGE : DEFAULT_PER_PAGE;
}
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;
/** One page of picker options. Products search server-side beyond it. */
const OPTION_PER_PAGE = 100;
/**
 * `/cities` caps at 200 and defaults to 50 — comfortably one page (28 rows at
 * last measurement, 2026-08-28; re-measure rather than trust it). Beyond a
 * page the picker searches server-side, which `/cities` supports on `name`
 * only.
 */
const CITY_PER_PAGE = 200;
/** `search` is `z.string().max(240)`; a longer term is a 422, not a truncation. */
const MAX_SEARCH = 240;

/**
 * The six the API orders by. A seventh would be a 422 on a `.strict()` query —
 * this is not a list the server ignores when it does not recognise a member.
 *
 * ⚠️ **`manual` is the DEFAULT and is the only order that can be REARRANGED.**
 * It is the hand-ordered `(sort_order, id)` sequence that
 * `PATCH /projects/{id}/position` writes; every other member is a computed
 * view of the same rows. Dragging under one of them would move a row to a
 * position nobody is looking at, so it is refused — *you can only reorder the
 * order you are actually looking at.*
 */
type ProjectSort = NonNullable<ProjectListParams['sort']>;
const SORT_KEYS: readonly ProjectSort[] = [
  'manual',
  'name',
  'createdAt',
  'updatedAt',
  'startDate',
  'endDate',
];

/** The one order rows may be dragged in. Named once; several branches ask. */
const MANUAL_SORT: ProjectSort = 'manual';

/**
 * A private drag type, so a row dragged out of this table cannot be dropped
 * into anything else and nothing else can be dropped into it. `text/plain` is
 * set alongside it purely so the gesture has a sensible payload if it ends up
 * over an editor or another application.
 */
const PROJECT_DRAG_MIME = 'application/x-emerge-project-id';

/** The two that order by a nullable column — see `DATE_SORT_NOTE`. */
const DATE_SORT_KEYS: readonly ProjectSort[] = ['startDate', 'endDate'];

const SORT_OPTIONS: readonly { value: string; label: string }[] = [
  /*
   * First because it is the endpoint's default and the one this list opens on.
   * Its `order` half is always `asc`: the manual sequence has a direction of
   * its own, so there is no "Manual order, reversed" to offer — see
   * `requestParams`, which sends no `order` with it at all.
   */
  { value: 'manual:asc', label: 'Manual order' },
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'updatedAt:desc', label: 'Recently updated' },
  { value: 'updatedAt:asc', label: 'Least recently updated' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  { value: 'startDate:asc', label: 'Starts soonest' },
  { value: 'startDate:desc', label: 'Starts latest' },
  { value: 'endDate:asc', label: 'Ends soonest' },
  { value: 'endDate:desc', label: 'Ends latest' },
];

/**
 * ⚠️ Shown only while a date sort is chosen — a FACT about where the rows
 * went, not advice. `start_date`/`end_date` are nullable, MySQL/MariaDB sort
 * `NULL` first ascending and last descending, and the server deliberately does
 * NOT COALESCE a sentinel date to smooth it over. So "Starts soonest" opens on
 * projects with no start date, which reads as a broken sort unless the reader
 * is told the rows are undated rather than misplaced.
 *
 * This is the *sorting* half of the nullability; the *filtering* half is
 * `PERIOD_FILTER_NOTE` below — a sort still shows an undated project, a filter
 * never does.
 */
const DATE_SORT_NOTE = 'Projects with no date sort first ascending, last descending.';

/**
 * ⚠️ Stated where the two range controls live: all four bounds compare against
 * a nullable column, so a project with no date recorded matches NEITHER bound
 * — the pair never partitions the list, and no period filter will ever return
 * an undated project. Without this, an empty result reads as "no projects in
 * this period" rather than "these have no date recorded".
 */
const PERIOD_FILTER_NOTE =
  'A project with no dates recorded matches neither bound, so a period filter never returns one.';

/**
 * Everything this page holds in the URL, which is everything the endpoint
 * accepts and nothing else. A filtered, sorted, paged view is therefore a real
 * link — `/projects?cycleId=5&isActive=true` survives a reload, a Back press
 * and a paste into a chat window.
 *
 * Ids are kept as STRINGS because that is what both `Combobox` and
 * `URLSearchParams` speak; they are parsed once, at the edge, in
 * `requestParams()`.
 */
interface ProjectFilters {
  search: string;
  productId: string;
  intakeId: string;
  cycleId: string;
  /** Projects that run in this city, via `f_project_cities`. */
  cityId: string;
  /**
   * The project's own region of focus. **Never a country's region** — see the
   * file header; the two share a word and nothing else.
   */
  regionId: string;
  /**
   * The four period bounds, `YYYY-MM-DD`, all INCLUSIVE (`start_date >=`,
   * `start_date <=`, and the same pair on `end_date`). '' is "no bound" and is
   * omitted from the request entirely — `?startDateAfter=` is a 422, not "no
   * filter", exactly like the empty ids above.
   */
  startDateAfter: string;
  startDateBefore: string;
  endDateAfter: string;
  endDateBefore: string;
  /** '' is "either", not `false` — the key is simply omitted. */
  isActive: '' | 'true' | 'false';
  sort: ProjectSort;
  order: ListOrder;
  page: number;
  perPage: number;
}

/** Anything that is not a positive integer is dropped, never forwarded. */
function positiveInt(raw: string | null): string {
  return raw !== null && /^[1-9]\d*$/.test(raw) ? raw : '';
}

/**
 * An id filter's URL value: one id or a comma-separated list (owner
 * 2026-10-28 — every filter takes several values). Each member is kept only if
 * it is a positive integer, duplicates are dropped, and the result is the
 * canonical CSV, so an old single-id bookmark reads back unchanged.
 */
function idList(raw: string | null): string {
  if (raw === null) return '';
  const ids = raw.split(',').map((part) => positiveInt(part.trim())).filter(Boolean);
  return [...new Set(ids)].join(',');
}

function splitIds(csv: string): string[] {
  return csv ? csv.split(',') : [];
}

/**
 * The same treatment for a date bound: anything that is not a real calendar
 * date is dropped rather than forwarded, because the server answers a 422
 * NAMING THE PARAMETER and a pasted `?startDateAfter=2026-02-30` would take the
 * whole list down rather than filtering it.
 *
 * `isRealDate` checks shape AND existence — `2026-02-30` fails the second — and
 * it is the app's one copy of that rule, shared with the catalog forms. ⚠️ No
 * `Date` is ever constructed from one of these values for comparison or
 * display: `new Date('2026-05-31')` is midnight UTC and renders as 30 May west
 * of Greenwich. They are compared as strings and formatted by `formatDateOnly`.
 */
function dateBound(raw: string | null): string {
  return raw !== null && isRealDate(raw) ? raw : '';
}

function readState(params: URLSearchParams): ProjectFilters {
  const rawActive = params.get('isActive');
  const rawSort = (params.get('sort') ?? '') as ProjectSort;
  const cycleId = idList(params.get('cycleId'));
  // The page size a bare URL means depends on the cycle filter — see
  // `CYCLE_PER_PAGE`. Read first, so both this and `writeState` agree on it.
  const rawPerPage = Number(positiveInt(params.get('perPage')) || String(defaultPerPage(cycleId)));
  const sort = SORT_KEYS.includes(rawSort) ? rawSort : MANUAL_SORT;

  return {
    // Truncated rather than sent whole: 241 characters is a 422 on a `.strict()`
    // schema, which would take the entire list down over a pasted paragraph.
    search: (params.get('search') ?? '').slice(0, MAX_SEARCH),
    productId: idList(params.get('productId')),
    intakeId: idList(params.get('intakeId')),
    cycleId,
    cityId: idList(params.get('cityId')),
    regionId: idList(params.get('regionId')),
    startDateAfter: dateBound(params.get('startDateAfter')),
    startDateBefore: dateBound(params.get('startDateBefore')),
    endDateAfter: dateBound(params.get('endDateAfter')),
    endDateBefore: dateBound(params.get('endDateBefore')),
    isActive: rawActive === 'true' || rawActive === 'false' ? rawActive : '',
    sort,
    /*
     * ⚠️ The manual order PINS `order` to `asc`, and a URL saying otherwise is
     * overruled rather than honoured. `?sort=manual&order=desc` would reverse
     * the very sequence the drag handles are anchored against — every "move up"
     * would visibly move a row down — so the pair is made unreachable here
     * rather than defended at each call site. `requestParams` omits the key
     * from the wire entirely; this value only keeps the Sort select's
     * `sort:order` value matching an option.
     */
    order: sort === MANUAL_SORT ? 'asc' : params.get('order') === 'asc' ? 'asc' : 'desc',
    page: Number(positiveInt(params.get('page')) || '1'),
    // Clamped, not forwarded: `?perPage=500` is a 422 and would read as a
    // broken page rather than as a bad link.
    perPage: Math.min(rawPerPage, MAX_PER_PAGE),
  };
}

/**
 * The request, written out key by key.
 *
 * Deliberately not a spread of the state above: the query schema is `.strict()`
 * and this is the one place that decides what leaves the app. An empty value is
 * omitted entirely — `?productId=` is a 422, not "no filter".
 */
function requestParams(state: ProjectFilters): ProjectListParams {
  const params: ProjectListParams = {
    page: state.page,
    perPage: state.perPage,
    sort: state.sort,
  };
  /*
   * ⚠️ **`order` IS SENT WITH `sort=manual`, and it must be.** This read
   * `if (state.sort !== MANUAL_SORT)` until 2026-08-30, on the belief that the
   * server's default direction for this sort was the one the position endpoint
   * writes. **It is not.** `order` defaults to `'desc'` and applies to `manual`
   * like every other member (`projects.schema.ts:353-361`), so an omitted key
   * yields `ORDER BY p.sort_order DESC, p.id DESC` — the hand-arranged sequence
   * read BACKWARDS — while `PATCH /projects/{id}/position` computes against
   * `sort_order ASC, id ASC` (`projects.repository.ts:684`). The list and the
   * anchors then disagree about which way the sequence runs: the page opens
   * upside down, and "move up" sends `{beforeId}` against a row that is LATER
   * in the real sequence, so the row travels the other way.
   *
   * `readState` already pins `order` to `'asc'` for the manual sort, so sending
   * `state.order` unconditionally is the whole fix — and it keeps the rule that
   * a request says what it wants rather than inheriting a default that can move.
   */
  params.order = state.order;
  if (state.search) params.search = state.search;
  // Sent as the CSV `readState` canonicalised: one id, or several (any of them).
  if (state.productId) params.productId = state.productId;
  if (state.intakeId) params.intakeId = state.intakeId;
  if (state.cycleId) params.cycleId = state.cycleId;
  if (state.cityId) params.cityId = state.cityId;
  if (state.regionId) params.regionId = state.regionId;
  /*
   * The four bounds go out as the strings they are — no parsing, no `Date`, no
   * normalising. `readState` has already refused anything that is not a real
   * calendar date, so '' here means "no bound" and the key is omitted; sending
   * `startDateAfter=''` would be a 422 rather than an unfiltered list.
   */
  if (state.startDateAfter) params.startDateAfter = state.startDateAfter;
  if (state.startDateBefore) params.startDateBefore = state.startDateBefore;
  if (state.endDateAfter) params.endDateAfter = state.endDateAfter;
  if (state.endDateBefore) params.endDateBefore = state.endDateBefore;
  // `listQuery()` keeps `false` and drops '' — so this is a real two-way filter
  // and the "either" case never reaches the wire at all.
  if (state.isActive) params.isActive = state.isActive === 'true';
  return params;
}

/**
 * Writes the state back to the query string, dropping anything that equals the
 * server's own default so there is ONE canonical URL per view — `/projects` and
 * `/projects?page=1&perPage=20&sort=createdAt&order=desc` are the same screen.
 */
function writeState(current: URLSearchParams, next: ProjectFilters): URLSearchParams {
  const params = new URLSearchParams(current);
  const put = (key: string, value: string, fallback: string) => {
    if (value && value !== fallback) params.set(key, value);
    else params.delete(key);
  };

  put('search', next.search, '');
  put('productId', next.productId, '');
  put('intakeId', next.intakeId, '');
  put('cycleId', next.cycleId, '');
  put('cityId', next.cityId, '');
  put('regionId', next.regionId, '');
  put('startDateAfter', next.startDateAfter, '');
  put('startDateBefore', next.startDateBefore, '');
  put('endDateAfter', next.endDateAfter, '');
  put('endDateBefore', next.endDateBefore, '');
  put('isActive', next.isActive, '');
  // Manual is the endpoint's default, so a bare `/projects` IS the manual order.
  put('sort', next.sort, MANUAL_SORT);
  /*
   * `order` is not a question the manual sequence answers, so it is removed
   * rather than defaulted — leaving a stale `order=desc` behind after somebody
   * switches back to manual would put a parameter in the URL that this page
   * overrules and the request omits, which is a link that lies about the view.
   */
  if (next.sort === MANUAL_SORT) params.delete('order');
  else put('order', next.order, 'desc');
  put('page', String(next.page), '1');
  // The default this compares against MOVES with the cycle filter — see
  // `CYCLE_PER_PAGE`. Reading it from `next` is what keeps one canonical URL.
  put('perPage', String(next.perPage), String(defaultPerPage(next.cycleId)));
  return params;
}

/** The narrowing id filters, for counting what is applied and clearing them. */
const ID_FILTER_KEYS = ['productId', 'intakeId', 'cycleId', 'cityId', 'regionId'] as const;

/**
 * The four period bounds, counted and cleared the same way.
 *
 * ⚠️ Each bound counts as one filter, not each PAIR as one — they are four
 * independent parameters. Undercounting matters: `narrowed` below uses this
 * count to choose between the "No projects match" and "No projects yet" empty
 * states, and a date filter that matched nothing while reading as unfiltered
 * would send somebody looking for records that are simply out of range.
 */
const DATE_FILTER_KEYS = [
  'startDateAfter',
  'startDateBefore',
  'endDateAfter',
  'endDateBefore',
] as const;

/**
 * What this page remembers against the account.
 *
 * ⚠️ **`cycleId` is in here, and it is the reason the default-cycle effect
 * grew a fourth state.** This page has two arrival writers now — the saved set
 * and `currentCycle()` — and both put a cycle in the URL. A saved set that
 * names NO cycle is a person who cleared it, so the derived default must stand
 * down rather than helpfully putting today's cycle back. See the effect.
 *
 * `isActive` is a filter with a control and belongs here. `perPage` does NOT —
 * it is derived from whether a cycle is set, and saving it would silently
 * defeat that derivation. `page` is never saved on any page.
 */
const SAVED_FILTER_KEYS = [
  'search',
  ...ID_FILTER_KEYS,
  ...DATE_FILTER_KEYS,
  'isActive',
] as const;
/**
 * No joint meaning with the filters, so merged per key: a link naming a cycle
 * says nothing about how the recipient likes their list ordered.
 */
const SAVED_VIEW_KEYS = ['sort', 'order'] as const;
type SavedProjectsKey = (typeof SAVED_FILTER_KEYS)[number] | (typeof SAVED_VIEW_KEYS)[number];

/** Cleared together by both "clear" affordances, so neither can drift. */
const CLEARED_FILTERS = {
  productId: '',
  intakeId: '',
  cycleId: '',
  cityId: '',
  regionId: '',
  startDateAfter: '',
  startDateBefore: '',
  endDateAfter: '',
  endDateBefore: '',
  isActive: '',
} as const satisfies Partial<ProjectFilters>;

function countActiveFilters(state: ProjectFilters): number {
  return (
    ID_FILTER_KEYS.filter((key) => state[key] !== '').length +
    DATE_FILTER_KEYS.filter((key) => state[key] !== '').length +
    (state.isActive ? 1 : 0)
  );
}

/* -------------------------------------------------------------------------
 * Icons — Lucide paths, drawn by hand rather than pulling in a dependency.
 * --------------------------------------------------------------------- */

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function ProjectsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18" />
      <path d="M8 4v6" />
      <path d="M8 15h8" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/** Lucide "tag", the same path as `TagIcon` in `components/catalog/icons.tsx`, at the row-action size. */
function PriceTagIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 8.5V4a1 1 0 0 1 1-1h4.5a2 2 0 0 1 1.42.59l9.5 9.5a2 2 0 0 1 0 2.82l-4.5 4.5a2 2 0 0 1-2.82 0l-9.5-9.5A2 2 0 0 1 3 8.5Z" />
      <circle cx="7.5" cy="7.5" r="1.25" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/* -------------------------------------------------------------------------
 * Filter option sources
 * --------------------------------------------------------------------- */

interface OptionSource {
  options: ComboboxOption[];
  /** How many rows the server holds, so a truncated list can say so. */
  total: number;
  loading: boolean;
  failed: boolean;
}

/**
 * Products for the product filter.
 *
 * `?active=` is deliberately NOT sent. A project's embedded objects resolve
 * with no active predicate — a product retired after the project was sold still
 * reads back with its name — so a filter that offered active products only
 * could not find those rows at all.
 *
 * `?productId=` matches "any of the products the project sells" (an `EXISTS`
 * over `f_project_products`, migration 156). *SUPERSEDED (owner 2026-10-28):*
 * "must not be made multi; `?productId=1,2` is a 422" — every id filter on
 * this endpoint now takes a comma-separated list, and this control sends one.
 */
function useProductFilterOptions(): OptionSource & { setTerm: (term: string) => void } {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /*
   * The term the loaded options answer — null until the first fetch settles.
   * Deriving `loading` from it rather than holding a flag means a new term
   * reads as loading during the very render that set it, with no setState
   * inside the effect to start a second render pass.
   */
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts(
        { page: 1, perPage: OPTION_PER_PAGE, ...(term ? { search: term } : {}) },
        controller.signal,
      )
      .then((result) => {
        setOptions(
          result.data.map((product) => ({
            value: String(product.id),
            label: product.productName,
            ...(product.productCode ? { hint: product.productCode } : {}),
          })),
        );
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedTerm(term);
      });
    return () => controller.abort();
  }, [term]);

  return { options, total, loading: loadedTerm !== term, failed, setTerm };
}

/**
 * Intakes for the intake filter, narrowed to the chosen cycle when there is
 * one. `/intakes` has no `search` param — an unknown key is stripped in silence
 * there, so wiring `onSearch` would look like it searched and quietly return
 * the same page. The Combobox filters the loaded list locally instead.
 */
function useIntakeFilterOptions(cycleId: string): OptionSource {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /** The cycle the loaded options answer — see `useProductFilterOptions`. */
  const [loadedCycleId, setLoadedCycleId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listIntakes(
        { page: 1, perPage: OPTION_PER_PAGE, ...(cycleId ? { cycleId } : {}) },
        controller.signal,
      )
      .then((result) => {
        setOptions(
          result.data.map((intake) => ({ value: String(intake.id), label: intake.intakeName })),
        );
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedCycleId(cycleId);
      });
    return () => controller.abort();
  }, [cycleId]);

  return { options, total, loading: loadedCycleId !== cycleId, failed };
}

/**
 * Cities for the city filter. `?isActive=` is omitted for the same reason as
 * the product filter above: a project's cities are read live with no active
 * predicate, so a filter restricted to active rows could never find a project
 * running in a since-retired city. Retiring a city is a *write*-time rule.
 *
 * Each option is labelled with its country, since city names are ambiguous
 * across countries. `search` is a real server parameter here (matches `name`
 * only), so `onSearch` is wired rather than left to the Combobox's local
 * filter.
 */
function useCityFilterOptions(): OptionSource & { setTerm: (term: string) => void } {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /** The term the loaded options answer — see `useProductFilterOptions`. */
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCities(
        { page: 1, perPage: CITY_PER_PAGE, ...(term ? { search: term } : {}) },
        controller.signal,
      )
      .then((result) => {
        setOptions(
          result.data.map((city) => ({
            value: String(city.id),
            label: city.name,
            ...(city.country ? { hint: city.country.name } : {}),
          })),
        );
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedTerm(term);
      });
    return () => controller.abort();
  }, [term]);

  return { options, total, loading: loadedTerm !== term, failed, setTerm };
}

/**
 * Keeps a selected filter renderable when its id is not in the loaded options —
 * the id arrived on the URL before the options did, the list is one page of a
 * longer one, or the row has since been retired.
 *
 * The loaded rows are the best label source available: every row matching a
 * `productId` filter carries that product's name. Failing that the bare id is
 * shown, because an empty trigger reads as "no filter" while the list stays
 * filtered.
 */
function withSelected(
  options: ComboboxOption[],
  value: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  // `value` is the filter's CSV; every selected id must stay renderable.
  const missing = splitIds(value).filter((id) => !options.some((option) => option.value === id));
  if (missing.length === 0) return options;
  return [
    ...missing.map((id): ComboboxOption => {
      const known = labels.get(id);
      return known
        ? { value: id, label: known }
        : { value: id, label: `${noun} #${id}`, hint: 'Not in the list below' };
    }),
    ...options,
  ];
}

/** A truncation report for an option list that is one page of a longer one. */
function truncationNote(source: OptionSource): string | undefined {
  return source.total > source.options.length
    ? `Showing the first ${source.options.length} of ${source.total}`
    : undefined;
}

/**
 * One id filter, several values (owner 2026-10-28). The URL holds the
 * selection as one comma-separated param; nothing selected is "all".
 *
 * ⚠️ `MultiCombobox` has no `onSearch`, so the Product and City lists filter
 * their loaded page locally; a truncated page says so in the footnote.
 */
function FilterField({
  label,
  value,
  options,
  onChange,
  allLabel,
  loading,
  failed,
  footnote,
  searchPlaceholder,
}: {
  label: string;
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  allLabel: string;
  loading: boolean;
  failed: boolean;
  footnote?: string | undefined;
  searchPlaceholder?: string;
}) {
  return (
    <Field
      label={label}
      {...(failed ? { error: `Couldn’t load ${label.toLowerCase()} options.` } : {})}
    >
      {({ id }) => (
        <MultiCombobox
          id={id}
          values={splitIds(value)}
          onChange={(values) => onChange(values.join(','))}
          options={options}
          placeholder={allLabel}
          loading={loading}
          {...(footnote ? { footnote } : {})}
          {...(searchPlaceholder ? { searchPlaceholder } : {})}
        />
      )}
    </Field>
  );
}

/**
 * One period bound PAIR — "Starts between", "Ends between".
 *
 * A `<fieldset>`/`<legend>` rather than two loose boxes, matching the
 * closing-date range on `/deals`: reads as one control per question, and a
 * screen reader announces "Starts between, from". The per-input labels stay
 * VISIBLE — a date box with nothing on it cannot say which end of the range
 * it is.
 *
 * Both values arrive already validated by `readState`, so `from`/`to` are
 * either '' or a real `YYYY-MM-DD` — the inverted-range test below is a plain
 * string comparison with no `Date` anywhere near it.
 *
 * ⚠️ The inverted-range message is an ERROR, not advice. The server accepts a
 * back-to-front pair and simply returns nothing, indistinguishable on this
 * page from "there are no projects in that period" — the bound is still
 * applied and still in the URL; the sentence only explains the empty result.
 */
function PeriodRangeFilter({
  legend,
  from,
  to,
  onFrom,
  onTo,
}: {
  legend: string;
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  const inverted = from !== '' && to !== '' && from > to;

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0 lg:col-span-2">
      <legend className="mb-1.5 p-0 text-xs font-medium text-fg-secondary">{legend}</legend>
      {/* Wraps onto two lines at 320px, side by side from `sm`. */}
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 basis-36">
          <Field label="From">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={from}
                onChange={(e) => onFrom(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="min-w-0 flex-1 basis-36">
          <Field
            label="To"
            {...(inverted ? { error: 'Before the “from” date.' } : {})}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                invalid={inverted}
                /*
                 * No `min`/`max`: a project runs in the past as readily as the
                 * future, and a native bound would silently make the inverted
                 * pair unreachable rather than explained.
                 */
                value={to}
                onChange={(e) => onTo(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </fieldset>
  );
}

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/*
 * The read-only cells that used to render these columns now live as inline
 * editors in `@/components/projects/ProjectInlineCells`, not duplicated here —
 * two implementations of one column's read view is how a table and the thing
 * you edit in it end up disagreeing about what a value means (e.g. the
 * `null` vs `0` capacity distinction).
 */

/* -------------------------------------------------------------------------
 * The columns
 * --------------------------------------------------------------------- */

/**
 * Every column the desktop table can draw, keyed once.
 *
 * ⚠️ **ONE ARRAY DRIVES THE HEADER, THE BODY AND THE SKELETON.** They were three
 * parallel lists, ~1,400 lines apart, and a column added to two of the three
 * does not error — the table simply draws one row shape over another heading and
 * every cell after it reads under the wrong label. `COLUMN_KEYS` is the order;
 * `PROJECT_COLUMNS` is what each one is.
 *
 * ⚠️ **TWO ENTRIES ARE COMPOSITE AND ARE STILL ONE COLUMN EACH.** `period` is
 * the cycle over its intake, `dates` is the start over the end — two writes and
 * two sort keys inside one cell. They are not splittable here: the header names
 * the pair, and the halves name themselves inside the cell.
 */
type ProjectColumnKey =
  | 'order'
  | 'name'
  | 'products'
  | 'period'
  | 'dates'
  | 'institutions'
  | 'partners'
  | 'capacity'
  | 'regions'
  | 'cities'
  | 'status'
  | 'updated'
  | 'actions';

interface ProjectColumn {
  /** The heading, and the word the column manager lists this column under. */
  label: string;
  /**
   * The header width and the body cap, DECLARED TOGETHER.
   *
   * ⚠️ **This pairing is the whole reason the descriptor exists.** The two used
   * to sit ~1,200 lines apart and are documented as having to move together —
   * left at an old value the cap silently clamps the column regardless of what
   * the header claims, and reordering the columns could separate them outright.
   * Here they cannot be moved independently.
   */
  th: string;
  td?: string;
  /**
   * The sort keys this heading speaks for. **Absent means the API cannot order
   * by this column** and the header carries no `aria-sort` at all — `aria-sort`
   * on an unsortable heading promises a control that does not exist, and
   * `sort=capacity` is a 422 on a `.strict()` query rather than a parameter the
   * server ignores.
   */
  sortKeys?: readonly ProjectSort[];
  /**
   * What this column contributes to the table's min-width FLOOR, in px — its own
   * declared header width. **The floor is not the sum of these** (see
   * `TABLE_FLOOR_PX`); the column's width is what the floor moves BY when the
   * column comes or goes.
   */
  floorPx: number;
}

/**
 * The DECLARED order. Anything the reader rearranges is a stored layout over
 * this, and a column added here appears for everybody rather than defaulting to
 * hidden — see `useColumnLayout`, which stores the HIDDEN keys.
 */
const COLUMN_KEYS: readonly ProjectColumnKey[] = [
  'order',
  'name',
  'products',
  'period',
  'dates',
  'institutions',
  'partners',
  'capacity',
  'regions',
  'cities',
  'status',
  'updated',
  'actions',
];

/**
 * ⛔ **NEITHER OF THESE MAY BE HIDDEN, AND THE REASONS ARE DIFFERENT.**
 *
 * `actions` is the row-actions lane — the ordinary lock, and the one the column
 * manager's own disabled-checkbox `title` is worded for.
 *
 * 🔴 `order` is the one that is easy to get wrong: it looks like a column of
 * decoration and it **hosts the ONLY drag grip on the page**. `draggable` is on
 * the `<TR>` and arms only for a press that started inside
 * `PROJECT_DRAG_HANDLE_SELECTOR`, which lives in `ProjectOrderControls` in this
 * cell and nowhere else. Hiding this column does not hide a value — it silently
 * removes manual reordering, with nothing failing and no control left to explain
 * the loss. The two move buttons go with it, so the touch and keyboard paths die
 * in the same click.
 *
 * ⚠️ It is also pinned FIRST (see `pinEnds`): a reorder that pushed it inboard
 * would put the grip in the middle of the row it drags.
 */
const LOCKED_COLUMN_KEYS: readonly ProjectColumnKey[] = ['order', 'actions'];

/**
 * Why each locked column is locked, in the reader's own terms.
 *
 * ⚠️ **ONE SENTENCE CANNOT COVER BOTH, which is why `ColumnManager` takes this
 * rather than baking a string in.** Its fallback — "this column cannot be
 * hidden" — is true of both and explains neither, and the Order column's reason
 * is the one nobody would guess: it is not row actions, it is the only drag
 * grip on the page.
 */
const LOCK_REASONS: Record<string, string> = {
  order: 'This column holds the only drag grip and the move buttons, so hiding it would remove reordering',
  actions: 'This column holds the row actions',
};

/**
 * Rendered with `pinned`, so its edge is not the reader's to choose.
 *
 * ⚠️ **NOT the same list as `LOCKED_COLUMN_KEYS`, and conflating them breaks the
 * thing this page most wants.** Locked is about hiding and reordering; pinned is
 * about which edge a cell already occupies. `order` is locked and **must stay
 * freezable** — a grip frozen to the left is exactly what a reader dragging rows
 * across a wide table wants. Only `actions` is `sticky right-0` already, and a
 * stored freeze on top of that would be a second, conflicting position rule.
 *
 * It is the same list `useFrozenOffsets` counts, so a column the reader freezes
 * right is offset by the actions lane rather than drawn over it.
 */
const PINNED_COLUMN_KEYS: readonly ProjectColumnKey[] = ['actions'];

const PROJECT_COLUMNS: Record<ProjectColumnKey, ProjectColumn> = {
  /*
    The reorder column, and the reason it carries no `sortKeys`: it is not a
    sortable column at all. It holds a drag grip and two move buttons, and it is
    present only for a reader who may write — the same treatment the Actions
    column gets, since both are controls rather than values. A reader loses no
    INFORMATION by its absence, which is the test the inline cells are mounted
    unconditionally for.
  */
  order: { label: 'Order', th: 'w-28', floorPx: 112 },
  /*
    Doubled from `w-80` per the owner — real project names run to "Campus
    Tours: Business Masters Conclave - India - Fall 2026", which wrapped to
    three lines beside a truncating Product column. The body cap travels with
    it in `td`, which is what stops the two drifting apart.
  */
  name: {
    label: 'Project',
    th: 'w-[40rem]',
    td: 'max-w-[40rem]',
    sortKeys: ['name'],
    floorPx: 640,
  },
  /*
    Plural since migration 156 — a project sells a SET. This table has no
    `table-fixed`, so `w-44` is a request the auto layout answers; the cell
    collapses past two chips into a count rather than widening the column.
  */
  products: { label: 'Products', th: 'w-44', td: 'max-w-[14rem]', floorPx: 176 },
  /*
    Period is the CYCLE and intake — the commercial window a project is sold in.
    Dates below is when it actually runs. Two questions, two columns: the server
    constrains neither against the other, so a project running outside its
    cycle's window is legal and folding the two would present one answer where
    there are two.
  */
  period: { label: 'Period', th: 'w-48', td: 'max-w-[16rem]', floorPx: 192 },
  /*
    Sized for the cell's widest state, not its rendered text: it holds an inline
    EDITOR, and a native `<input type="date">` needs roughly 150px for
    `dd/mm/yyyy` plus its calendar button, so a width cut to fit "31 May 2026"
    would be right in the idle state and too narrow the moment anybody clicked
    it.

    ⚠️ `w-44` → `w-60` (2026-08-30) because each bound now carries a visible
    "Starts"/"Ends" label — a `w-11` label plus `gap-2`, ~52px, taken out of the
    same cell the editor sits in. Cut the label and this goes back to `w-44`;
    keep the label and cut this, and the editor is squeezed instead.

    ⚠️ **TWO sort keys on ONE heading.** `startDate` and `endDate` share this
    column, and marking only one would leave it reading as unsorted half the
    time it is sorted.
  */
  dates: {
    label: 'Dates',
    th: 'w-60',
    td: 'max-w-[18rem]',
    sortKeys: ['startDate', 'endDate'],
    floorPx: 240,
  },
  /*
    Deliberately not `numeric` (right-aligned): an editable cell's trigger
    carries its pencil at the end and its editor is a full-width input, and
    right-aligning either would put the affordance on the opposite side from
    every other column.
  */
  /*
    Planned capacity and target per category (owner 2026-10-27). The ids
    `institutions` / `partners` are stored in layouts — never rename them.
    `capacity` keeps its id and is now the read-only derived total.
  */
  institutions: { label: 'Institutions', th: 'w-44', td: 'max-w-[14rem]', floorPx: 176 },
  partners: { label: 'Partners', th: 'w-44', td: 'max-w-[14rem]', floorPx: 176 },
  capacity: { label: 'Total capacity', th: 'w-28', floorPx: 112 },
  /*
    Plural since migration 095 (2026-08-29): a project declares a SET of
    regions, so the heading names what the cell holds. Chips wrap inside the
    cell's `max-w-[12rem]`.
  */
  regions: { label: 'Regions', th: 'w-40', td: 'max-w-[12rem]', floorPx: 160 },
  cities: { label: 'Cities', th: 'w-72', td: 'max-w-[18rem]', floorPx: 288 },
  status: { label: 'Status', th: 'w-28', floorPx: 112 },
  updated: {
    label: 'Updated',
    th: 'w-44',
    td: 'text-xs whitespace-nowrap text-fg-muted tabular',
    sortKeys: ['updatedAt'],
    floorPx: 176,
  },
  /*
    Pinned to the right edge of the scroller — unpinned, this column sat past
    the viewport edge even at 1440px (measured on production: the cell ran 181px
    clear of the edge and `elementFromPoint` at each button returned null),
    leaving Edit/Delete unreachable without dragging the table sideways.

    ⚠️ **`pinned` IS `frozen="right"` AT OFFSET ZERO**, which `Table.tsx` says in
    as many words, so this column stays on the shorthand rather than being
    migrated. What it must NOT do is sit outside the freeze model: it is named in
    `PINNED_COLUMN_KEYS` and handed to `useFrozenOffsets`, or a column the reader
    freezes right would be handed offset 0 and drawn straight over these buttons.
  */
  /*
    Sized for the full set of THREE buttons — Prices, Edit, Delete (Prices added
    2026-10-27). Narrower sets come from `ACTIONS_NARROWER` below.
  */
  actions: { label: 'Actions', th: 'w-32', floorPx: 128 },
};

/**
 * The Actions lane with fewer buttons than the full three (Prices is shown to
 * every reader, so the lane always holds at least one).
 *
 * The class and the floor contribution are the same fact twice, so they are
 * narrowed together in one place rather than branched at each use.
 */
const ACTIONS_ONE_BUTTON = { th: 'w-16', floorPx: 64 } as const;
const ACTIONS_TWO_BUTTONS = { th: 'w-24', floorPx: 96 } as const;

function columnsFor(actionCount: number): Record<ProjectColumnKey, ProjectColumn> {
  const narrower =
    actionCount === 1 ? ACTIONS_ONE_BUTTON : actionCount === 2 ? ACTIONS_TWO_BUTTONS : null;
  if (!narrower) return PROJECT_COLUMNS;
  return { ...PROJECT_COLUMNS, actions: { ...PROJECT_COLUMNS.actions, ...narrower } };
}

/**
 * The table's min-width floor with every column drawn and all three row actions.
 *
 * ⚠️ **NOT the sum of the header widths, and never was.** Those come to 2336.
 * Under `table-layout: auto` a `<th>` width is a REQUEST that content outranks,
 * compressed by the algorithm when the table is narrower than the total, and the
 * `max-w-` caps on the body cells absorb that. The two numbers are allowed to
 * disagree — treating this as a sum will send you re-cutting numbers that are
 * not wrong.
 *
 * What it IS: the floor this table was measured at with everything showing. A
 * column that disappears takes its own declared width back out of it, which is
 * the relationship the two literals it replaces already expressed — 1900 without
 * the Order column, 2012 with it, a delta of exactly its `w-28`.
 */
// 2012 + the Institutions and Partners columns (2 × 176) = 2364, + the Prices
// row action (Actions `w-24` → `w-32`, 32) = 2396, both 2026-10-27.
const TABLE_FLOOR_PX = 2396;

/** Every declared column's width, so a hidden one can be subtracted from the floor. */
const FULL_COLUMNS_PX = COLUMN_KEYS.reduce((px, key) => px + PROJECT_COLUMNS[key].floorPx, 0);

/**
 * The floor for the columns actually on screen.
 *
 * ⚠️ **It goes through `style`, never a class.** Tailwind reads class names out
 * of the source text, so a computed `min-w-[${n}px]` generates no rule at all
 * and the table quietly loses its floor — the columns then compress instead of
 * the wrapper scrolling. `Table` takes no `style` prop and is a shared
 * primitive, so the floor is carried by a wrapper the `w-full` table fills.
 */
function tableFloorPx(
  columns: Record<ProjectColumnKey, ProjectColumn>,
  visibleKeys: readonly ProjectColumnKey[],
): number {
  const shownPx = visibleKeys.reduce((px, key) => px + columns[key].floorPx, 0);
  return Math.max(0, TABLE_FLOOR_PX - (FULL_COLUMNS_PX - shownPx));
}

/**
 * Order leads and Actions closes, whatever the stored layout says.
 *
 * ⚠️ **Belt and braces, and both halves are load-bearing.** The manager clamps
 * every move into the band between them, so a stored order should never place
 * them anywhere else — but the preferences document is unvalidated by design and
 * can be written by an older build or by hand, and a grip that turned up in the
 * middle of the row would look like a layout bug rather than like bad data.
 */
function pinEnds(keys: readonly ProjectColumnKey[]): readonly ProjectColumnKey[] {
  const middle = keys.filter((key) => key !== 'order' && key !== 'actions');
  return [
    ...(keys.includes('order') ? (['order'] as const) : []),
    ...middle,
    ...(keys.includes('actions') ? (['actions'] as const) : []),
  ];
}

/* -------------------------------------------------------------------------
 * Loading shapes
 * --------------------------------------------------------------------- */

/**
 * The loading shape of one cell.
 *
 * ⚠️ **Each one is the LOADED cell's shape, not a bar.** A one-line skeleton
 * under a two-line cell makes every row grow taller the moment data lands, and
 * a column missing here makes the whole table jump sideways instead — which is
 * why this is driven by the same key list as the header and the body rather
 * than being a parallel list that can fall one column behind.
 */
function skeletonCell(key: ProjectColumnKey, actionCount: number) {
  switch (key) {
    case 'order':
      return (
        <div className="flex gap-1">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-6 w-6" />
        </div>
      );
    case 'name':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-2.5 w-32" />
        </div>
      );
    case 'products':
      return <Skeleton className="h-3 w-28" />;
    // Cycle over intake — one stacked cell, as the loaded row draws it.
    case 'period':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      );
    // Start over end, in the real two-line shape.
    case 'dates':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-24" />
        </div>
      );
    // Left-aligned, matching the editable cell (not right-aligned like the old
    // read-only capacity column).
    case 'institutions':
    case 'partners':
      return <Skeleton className="h-3 w-24" />;
    case 'capacity':
      return <Skeleton className="h-3 w-8" />;
    case 'regions':
      return <Skeleton className="h-3 w-24" />;
    case 'cities':
      return (
        <div className="flex gap-1">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
      );
    case 'status':
      return <Skeleton className="h-4 w-16" />;
    case 'updated':
      return <Skeleton className="h-3 w-28" />;
    case 'actions':
      return (
        <div className="flex justify-end gap-1.5">
          {Array.from({ length: actionCount }, (_, n) => (
            <Skeleton key={n} className="h-6 w-6" />
          ))}
        </div>
      );
  }
}

function TableSkeleton({
  columns,
  visibleKeys,
  actionCount,
  frozenSideOf,
  frozenOffsets,
}: {
  columns: Record<ProjectColumnKey, ProjectColumn>;
  visibleKeys: readonly ProjectColumnKey[];
  actionCount: number;
  frozenSideOf: (key: ProjectColumnKey) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {visibleKeys.map((key) => (
            <TD
              key={key}
              className={columns[key].td}
              numeric={key === 'actions'}
              pinned={key === 'actions'}
              /*
               * ⚠️ `actions` renders through `pinned`, so it must take NO
               * `frozen` — two position rules on one cell is a race. The
               * controller already refuses to freeze a pinned key; this guard
               * is against a preferences document written by something else,
               * exactly as `pinEnds` is.
               */
              frozen={key === 'actions' ? undefined : frozenSideOf(key)}
              frozenOffset={key === 'actions' ? undefined : frozenOffsets[key]}
            >
              {skeletonCell(key, actionCount)}
            </TD>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="border-b border-border px-4 py-3 last:border-b-0">
          <div className="grid gap-1.5">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-2.5 w-28" />
            <Skeleton className="h-2.5 w-36" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------
 * The page
 * --------------------------------------------------------------------- */

export function ProjectsPage() {
  const { ability } = useAuth();

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /projects` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The write gates below are untouched.
   */
  const canReadModule = ability.canRead('projects');

  /*
   * Every filter, the sort, the page and the page size live in the URL.
   *
   * `useSearchParams()` takes NO argument — its first parameter is a default
   * init, not options, so `useSearchParams({ replace: true })` would write a
   * literal `replace=true` into the query string and then into the parsed
   * filters. Every write passes `{ replace: true }` to the SETTER instead, so
   * changing a filter is not a navigation and Back does not mean "undo one
   * filter" a dozen times.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // Depend on the string, never on the object: its identity changes every render.
  const searchKey = searchParams.toString();
  const state = useMemo(() => readState(new URLSearchParams(searchKey)), [searchKey]);

  /*
   * Saved filters, against the EMERGE account rather than this browser.
   *
   * ⚠️ **`perPage` is NOT saved here, and this is the one page where that is a
   * decision rather than an omission.** `defaultPerPage(cycleId)` derives the
   * size from whether a cycle is set — a whole cycle is shown at once so it can
   * be ordered end to end — and `writeState` omits the key whenever it equals
   * that derived default. A saved `perPage=20` would be indistinguishable from
   * somebody having chosen 20, and would defeat the cycle default silently, for
   * one account, for ever.
   *
   * ⚠️ `sort` and `order` ARE saved, and they interact with the manual order:
   * `readState` pins `order` to `asc` under the manual sort, so a saved
   * `order=desc` cannot corrupt it — the read normalises before anything uses
   * it. That is worth knowing before adding `perPage` back on a quiet day.
   */
  const applySavedFilters = useSavedFilterApply<SavedProjectsKey>();
  const savedFilters = useSavedFilters({
    scope: 'projects',
    keys: SAVED_FILTER_KEYS,
    independentKeys: SAVED_VIEW_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Project> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState('more-filters', false);
  /**
   * Bumped by a save or a delete to re-run the same query. A refetch is an
   * EVENT, so it is expressed as one rather than as a function that flips a
   * loading flag from inside an effect.
   */
  const [refreshToken, setRefreshToken] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /**
   * True when the failure was the 409 — the project is still named by a quote,
   * invoice or deal LINE and the server refused.
   *
   * Held apart from the message rather than baked into it because the message
   * is the SERVER's, verbatim: it is the only thing that knows the counts, and
   * re-wording it would drop them. This flag is what lets the banner add the
   * one sentence the server does not say — retire it instead.
   */
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  /**
   * One writer for the whole query string.
   *
   * Any change other than paging resets to page 1: page 4 of the old result set
   * is meaningless in the new one, and a request for a page past the end comes
   * back empty, which reads as "nothing matches" rather than "you are past the
   * end".
   */
  const update = useCallback(
    (patch: Partial<ProjectFilters>) => {
      const current = new URLSearchParams(searchKey);
      const next: ProjectFilters = { ...readState(current), ...patch };
      if (patch.page === undefined) next.page = 1;
      /*
       * ⚠️ **The page size a cycle filter implies has to FOLLOW the filter.**
       * `readState` resolved `perPage` against the cycle that was set a moment
       * ago, so without this, choosing a cycle would carry the old default (20)
       * forward as though somebody had picked it — writing `perPage=20` into
       * the URL and defeating the whole point of `CYCLE_PER_PAGE`, silently and
       * only for people who arrived without a cycle.
       *
       * Only when nobody has CHOSEN a size: an explicit `?perPage=` in the URL
       * is a decision and survives every filter change. Same shape as the
       * default cycle itself — a default may be re-derived, a choice may not.
       */
      if (patch.cycleId !== undefined && positiveInt(current.get('perPage')) === '') {
        next.perPage = defaultPerPage(next.cycleId);
      }
      setSearchParams(writeState(current, next), { replace: true });
    },
    [searchKey, setSearchParams],
  );

  /*
   * The search box types into local state and commits to the URL 250ms later.
   *
   * Debouncing the URL write rather than the fetch keeps ONE debounce in play:
   * every other control writes the URL immediately and the fetch below fires on
   * whatever the URL says. Debouncing both would delay a filter click by a
   * quarter of a second for no reason, and firing a request per keystroke is
   * the fastest way into the rate limit, which is shared by everyone behind the
   * office NAT. ⚠️ *SUPERSEDED: "the 120-per-60s rate limit."* It is
   * **12,000/60s** (corrected 2026-09-17) — a request per keystroke is still
   * the fastest way into it.
   */
  const [searchInput, setSearchInput] = useState(state.search);
  const [lastUrlSearch, setLastUrlSearch] = useState(state.search);
  if (lastUrlSearch !== state.search) {
    // The URL changed under us — Back, a pasted link, or our own commit. Adopt
    // it during this render rather than in an effect, which would paint the old
    // term for a frame first.
    setLastUrlSearch(state.search);
    setSearchInput(state.search);
  }

  useEffect(() => {
    if (searchInput === state.search) return;
    const timer = setTimeout(() => update({ search: searchInput }), 250);
    return () => clearTimeout(timer);
  }, [searchInput, state.search, update]);

  const params = useMemo(() => requestParams(state), [state]);

  /*
   * What the screen is currently asking for. `loading` is DERIVED from it
   * rather than held in its own flag: the request is fired by the URL changing,
   * so a flag would have to be set from inside the effect, and that is both a
   * cascading render and the thing that goes stale if a write ever fails to
   * produce a matching clear.
   */
  const requestKey = `${searchKey}#${refreshToken}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loading = loadedKey !== requestKey;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listProjects(params, signal)
      .then((data) => {
        setResult(data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setError(caught instanceof Error ? caught.message : 'Failed to load projects');
      })
      .finally(() => {
        // An aborted request has been superseded by another one that will do
        // this itself — marking it loaded here would clear the spinner for a
        // result nobody is waiting on.
        if (!signal.aborted) setLoadedKey(requestKey);
      });
    return () => controller.abort();
  }, [params, requestKey]);

  /** Re-runs the current query — after a save, or after a delete in place. */
  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  /*
   * Creating and editing a project takes any write role — POST and PATCH are
   * `requireRole(...WRITE_ROLES)` on the router, so a basic_user and an
   * advanced_user belong here. DELETE alone is `ADMIN_ROLES`, and `canDelete`
   * is the single place that knows it: never `role === 'admin'`, which would
   * silently exclude `super_admin`.
   */
  const canCreate = ability.canCreate(PROJECTS);
  const canUpdate = ability.canUpdate(PROJECTS);
  const canDelete = ability.canDelete(PROJECTS);
  // Prices is offered to every reader (the editor is read-only without
  // `projects.update`), so the lane always holds at least one button.
  const actionCount = 1 + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  /** The project whose prices are open, or null. One dialog per page, not per row. */
  const [pricingProject, setPricingProject] = useState<Project | null>(null);

  // Keyed on `result` so the identity is stable between renders: `?? []` is a
  // fresh array every time, which would make every memo below it do nothing.
  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  // 0, not 1, for an empty set — see file header.
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (state.page - 1) * state.perPage + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  const products = useProductFilterOptions();
  const intakes = useIntakeFilterOptions(state.cycleId);
  const cities = useCityFilterOptions();
  const cycles = useCycleOptions();

  /*
   * ⚠️ Regions come through `useReferenceOptions`, which sends `isActive: true`
   * (and `perPage: 200`). **Keep sending it**, and note that today it narrows
   * NOTHING — which is exactly why it is easy to drop and hard to notice.
   *
   * ⚠️ **This comment claimed "only 5 of the 22 rows in `f_regions` are active,
   * so a bare fetch would offer 17 guaranteed 422s". Corrected 2026-08-29: the
   * master holds 6 rows and every one is active.** The 17 were DELETED, not
   * retired — the opposite of the rule the other reference masters follow — so
   * an unfiltered fetch looks correct today and breaks the first time anybody
   * retires a region. A write still requires an ACTIVE id, so the reason the
   * filter is here has not changed; only the data has.
   *
   * Cost: a region retired *after* a project was saved is no longer in the
   * options, so the trigger would read "All regions" while the list stayed
   * filtered. `withSelected` below closes that from the rows on screen.
   */
  const regions = useReferenceOptions('regions');

  const cycleOptions = useMemo<ComboboxOption[]>(
    () => cycles.cycles.map((cycle) => ({ value: String(cycle.id), label: cycle.cycleName })),
    [cycles.cycles],
  );

  /*
   * ── The Cycle filter starts on the cycle running TODAY ───────────────────
   *
   * A bare `/projects` arrival shows the current cycle rather than every
   * project ever run, via `currentCycle()` in `src/lib/document-naming.ts` —
   * the same helper that names a new deal or quote, so there is ONE
   * definition of "the cycle running today". It compares `YYYY-MM-DD` strings
   * built from LOCAL date parts (not `new Date()`/`toISOString()`, both UTC)
   * and matches on the date window, not "the newest row" (live cycles do not
   * arrive in date order).
   *
   * ⚠️ **The resolved id is written INTO the URL, once — that's what keeps the
   * filter CLEARABLE.** The naive version ("no cycle named -> request the
   * current one") cannot be cleared: clearing removes the param, the
   * condition is true again, and the default re-applies. `defaultCycleSettled`
   * is what makes it happen only once, distinguishing the three states that
   * look identical in the URL:
   *
   *   1. first arrival, no cycle named   -> apply the default
   *   2. an explicit `?cycleId=N`        -> settled untouched; N WINS
   *   3. the user cleared the filter     -> already settled; stays cleared
   *
   * A ref, not state: nothing renders from it.
   *
   * ⚠️ **"Once" means once per MOUNT, deliberately.** Leaving the page and
   * coming back re-seeds the default (measured, not assumed) — a cleared
   * filter is not a preference worth persisting across visits. If that's ever
   * wanted, it's a stored flag, not a change to this effect.
   */
  const defaultCycleSettled = useRef(false);

  useEffect(() => {
    if (defaultCycleSettled.current) return;

    /*
     * ⚠️ **STATE 4, ADDED WITH SAVED FILTERS: the account already answered.**
     *
     * This page now has TWO writers that put filters into the URL on arrival,
     * and they must not race or a reader gets whichever landed second. So this
     * one waits for the other to decide, and stands down when it did anything:
     *
     *   - `seeding` — the preferences read is still out (bounded, so this
     *     cannot hang). Deciding now would seed a default the saved set is
     *     about to overwrite, and the reader would see the cycle flip.
     *   - `'seeded'` — saved filters applied. **Including when they contain no
     *     cycle at all**, which is the important half: a person who cleared the
     *     cycle filter and had that saved has answered this question with
     *     "none", and re-deriving today's cycle over it is the exact
     *     cleared-means-cleared failure the ref below exists to prevent — just
     *     arriving from storage instead of from the URL.
     *
     * `'link-wins'` and `'nothing-saved'` fall through to the behaviour that
     * was already here, unchanged.
     */
    if (savedFilters.seeding) return;
    // `'remembered'`: filter memory answered (owner 2026-10-16) — a remembered
    // current cycle is already restored as today's, a remembered clear stands.
    if (savedFilters.arrival === 'seeded' || savedFilters.arrival === 'remembered') {
      defaultCycleSettled.current = true;
      return;
    }

    /*
     * State 2. `intakeId` settles this too: an intake belongs to exactly ONE
     * cycle, so layering a possibly-different cycle over `?intakeId=` would
     * produce a pair that can only ever return nothing.
     */
    if (state.cycleId || state.intakeId) {
      defaultCycleSettled.current = true;
      return;
    }

    // Not an answer yet — settling here would decide "no cycle contains
    // today" from a request that hasn't come back.
    if (cycles.loading) return;

    /*
     * Every branch below settles, including the two that apply nothing. A
     * failed `/cycles` read is not evidence about the calendar, and no cycle
     * containing today is an ORDINARY state (between business years) — both
     * leave the filter unset rather than guessing a nearest cycle, and
     * neither retries. `cycles.hasMore` needs no branch: a current cycle past
     * the first page just lands in the "no default" case (cycles are
     * business years, so unreachable in practice).
     */
    defaultCycleSettled.current = true;
    if (cycles.failed) return;

    const running = currentCycle(cycles.cycles);
    if (!running) return;

    /*
     * `update` passes `{ replace: true }`, so seeding the default is not a
     * history entry. It resets to page 1 (a `?page=3` deep link with no cycle
     * is a page number for a different result set), and the in-flight
     * unfiltered request is aborted by the list effect's own cleanup.
     */
    update({ cycleId: String(running.id) });
  }, [
    state.cycleId,
    state.intakeId,
    cycles.loading,
    cycles.failed,
    cycles.cycles,
    update,
    // Both are read above, and `seeding` going false is what re-runs this
    // effect once the preferences read has settled.
    savedFilters.seeding,
    savedFilters.arrival,
  ]);

  const regionOptions = useMemo<ComboboxOption[]>(
    // Rows arrive in curated picker order (`sortOrder`, then `name`) — do not
    // re-sort them alphabetically here.
    () => regions.records.map((region) => ({ value: String(region.id), label: region.name })),
    [regions.records],
  );

  /*
   * id → name harvested from the rows on screen. Cheap, and the only label
   * source for a value the option lists cannot offer — a retired product, or a
   * cycle beyond the first page.
   */
  const rowLabels = useMemo(() => {
    const maps = {
      productId: new Map<string, string>(),
      intakeId: new Map<string, string>(),
      cycleId: new Map<string, string>(),
      cityId: new Map<string, string>(),
      regionId: new Map<string, string>(),
    };
    for (const row of result?.data ?? []) {
      // The embedded catalog objects spell it `name` here. `d_deliverables`
      // serialises the SAME three tables as productName / intakeName /
      // cycleName — two spellings of one concept, and this is the short one.
      /*
       * ⚠️ Every product the project sells, not the deprecated singular
       * `product` — migration 156. The FILTER is still `?productId=`, one id at
       * a time, and it now matches "projects that INCLUDE this product", so a
       * project's secondary product is a value the filter can legitimately hold.
       * Harvesting only the primary would leave the selected-filter chip reading
       * a bare id for exactly those.
       */
      for (const product of row.products) maps.productId.set(String(product.id), product.name);
      if (row.intake) maps.intakeId.set(String(row.intake.id), row.intake.name);
      if (row.cycle) maps.cycleId.set(String(row.cycle.id), row.cycle.name);
      // A project's regions are a SET since migration 095 (2026-08-29). The
      // FILTER is still the singular `?regionId=` — measured, still a 200 — so
      // this map stays keyed on one id at a time; every member of every row's
      // set is a value the filter can hold.
      for (const region of row.regions) maps.regionId.set(String(region.id), region.name);
      // Labelled with the country here too, for the same reason as everywhere
      // else: the selected-filter chip must not say "Birmingham" alone.
      for (const city of row.cities) maps.cityId.set(String(city.id), cityLabel(city));
    }
    return maps;
  }, [result]);

  /* ── Editing a row in place ───────────────────────────────────────────────
   *
   * Every writable column on this list is editable where it is read. The Edit
   * modal STAYS — it is the better tool for changing several fields at once,
   * and it is the only route to the two states the inline pickers deliberately
   * cannot reach (a blank required field, and creating a project at all).
   *
   * The cells live in `@/components/projects/ProjectInlineCells`, which owns the
   * contract rules; this page owns only the three things a cell cannot know:
   * who may write, where the option lists come from, and how a saved record
   * gets back into the row.
   */

  /**
   * The saved record, applied to the loaded row — never a refetch.
   *
   * ⚠️ **A refetch here reads as a FAILED SAVE.** Being asynchronous, the row
   * would re-render from the STALE record between the draft clearing and the
   * new page arriving, visibly flicking back to the old value under a "Saved."
   * message.
   *
   * `patch` is a PARTIAL on purpose: each cell passes only the keys it owns.
   * Two cells on one row can be in flight at once and resolve out of order, so
   * merging whole records would let the slower response revert the faster
   * one's change.
   */
  const patchRow = useCallback((id: number, patch: Partial<Project>) => {
    setResult((current) =>
      current
        ? {
            ...current,
            data: current.data.map((row) => (row.id === id ? { ...row, ...patch } : row)),
          }
        : current,
    );
  }, []);

  /* ── Rearranging the manual order ─────────────────────────────────────────
   *
   * `PATCH /projects/{id}/position` takes an ANCHOR — `{beforeId}` or
   * `{afterId}`, never an index — because `sort_order` is one GLOBAL sequence
   * while this page shows a filtered, paged SLICE of it that the server cannot
   * see. So every move is expressed against **a row that is on screen**:
   * "to the top" means `{beforeId: <the first VISIBLE row>}`, not "to the top
   * of the table". `use-project-order.ts` holds the whole model.
   *
   * Three gates, and each is a different question:
   *
   *  - `manualOrder` — you can only rearrange the order you are LOOKING at. A
   *    drag under "Name A–Z" would write a position nobody can see, so it is
   *    refused with a sentence rather than silently doing something surprising;
   *  - `canUpdate` — the same write role that edits a project. Never the role
   *    at a call site;
   *  - a move onto ANOTHER PAGE is inexpressible, there being no row from it to
   *    anchor on. That is not gated — reordering within the page is correct as
   *    far as it goes — it is STATED, in the notice below the filters.
   */
  const manualOrder = state.sort === MANUAL_SORT;
  /** Both gates in one word — the drag handlers are attached only under it. */
  const canReorder = manualOrder && canUpdate;

  /**
   * Applies a rearrangement to the loaded page.
   *
   * Takes an ordering FUNCTION rather than a finished array so a rollback can
   * re-apply the previous id order to whatever is on screen at that moment —
   * every column here is editable in place, and restoring a snapshot array
   * would revert an inline save that landed during the move.
   */
  const reorderRows = useCallback((ordering: ProjectOrdering) => {
    setResult((current) => (current ? { ...current, data: ordering(current.data) } : current));
  }, []);

  /**
   * The refusal, worded once for the three places that need it: the disabled
   * handles, the 403 branch, and nothing else. `denialReason` is the app's own
   * sentence for "why can't I"; the fallback covers a reader who is denied for
   * a reason the ability model has no words for.
   */
  const updateRefusal =
    denialReason(ability, 'update', PROJECTS) ??
    'Your account does not have permission to reorder projects.';

  const order = useProjectOrder({
    rows,
    reorder: reorderRows,
    enabled: canReorder,
    // A role can be taken away under a live access token (about five days), so
    // the hidden column is the optimisation and this is the guarantee.
    forbiddenMessage: updateRefusal,
    reload: refresh,
  });

  /*
   * ⚠️ Not a bare `disabled` anywhere: a handle with no reason makes "you may
   * not write projects" and "you are looking at a sorted view" the same dead
   * control. `null` means the row can be moved.
   */
  const orderDisabledReason = !canUpdate
    ? updateRefusal
    : manualOrder
      ? null
      : 'Only the manual order can be rearranged — switch the sort to Manual order.';

  /*
   * THE ROW A PRESS HAS PUT INTO THE DRAG, and why it has to exist.
   *
   * `draggable` has to sit on the `<tr>` for the drag image to be the row, but
   * per the HTML drag spec the drag source is the nearest draggable ANCESTOR —
   * so a permanently draggable row would be dragged by a press-and-twitch on
   * ANY of the nine inline editors inside it, and the drag then swallows the
   * click that would have opened the editor. Nothing throws; the cell simply
   * stops responding.
   *
   * So the row is draggable only for a press that began inside the grip.
   * `KanbanBoard` does the mirror image of this — one child taken OUT of a
   * permanently draggable card — for the same spec reason.
   *
   * ONE id, not a set: there is one primary pointer.
   */
  const [armedRowId, setArmedRowId] = useState<number | null>(null);
  /** The row being dragged, and the row under the pointer. */
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  const endDrag = useCallback(() => {
    setArmedRowId(null);
    setDraggingId(null);
    setDropTargetId(null);
  }, []);

  /*
   * ⚠️ **The write-time option lists are a SECOND pair of requests, and that
   * is not duplication.** The Product and City *filters* above deliberately
   * fetch without an active predicate, so a filter can still find a project
   * with a retired product/city. A picker that WRITES needs the opposite: an
   * inactive id is a 422. One list cannot answer both questions. Regions are
   * the exception and are shared, since `useReferenceOptions` already sends
   * `?isActive=true`.
   *
   * Gated on `canUpdate` — a reader has nothing to fill a picker with, so the
   * request buys nothing, and the limiter is shared across the office NAT.
   * ⚠️ *SUPERSEDED: "120 requests per 60 seconds."* It is **12,000/60s**
   * (corrected 2026-09-17).
   */
  const writeOptions = useProjectWriteOptions(canUpdate);
  const intakesFor = useIntakesByCycle(rows, canUpdate);

  const editContext = useMemo<ProjectEditContext>(
    () => ({
      /*
       * `denialReason` rather than a sentence of our own, and computed ONCE for
       * eight controls so they cannot word one refusal eight ways. It is `null`
       * when the reader may write, which is what every cell tests.
       *
       * ⚠️ A `viewer` must still SEE these values, so the cells are mounted
       * unconditionally and disable themselves from this. `{canUpdate && …}`
       * would hide the data, and read on `/projects` is open to every role.
       */
      denial: canUpdate ? null : (denialReason(ability, 'update', PROJECTS) ??
        'Your account does not have permission to edit projects.'),
      products: writeOptions.products,
      cities: writeOptions.cities,
      activeCityIds: writeOptions.activeCityIds,
      cycles: {
        options: cycleOptions,
        loading: cycles.loading,
        failed: cycles.failed,
        truncated: cycles.hasMore,
        total: cycleOptions.length,
      },
      regions: { records: regions.records, loading: regions.loading, failed: regions.failed },
      intakesFor,
      onSaved: patchRow,
    }),
    [
      ability,
      canUpdate,
      writeOptions.products,
      writeOptions.cities,
      writeOptions.activeCityIds,
      cycleOptions,
      cycles.loading,
      cycles.failed,
      cycles.hasMore,
      regions.records,
      regions.loading,
      regions.failed,
      intakesFor,
      patchRow,
    ],
  );

  const filterCount = countActiveFilters(state);
  const narrowed = Boolean(state.search) || filterCount > 0;

  /*
   * The Order column is a column of CONTROLS, so it follows the Actions column
   * and is omitted for a reader who cannot use it — rather than the inline
   * cells, which are mounted unconditionally because withholding one would
   * withhold a VALUE. Nothing is hidden here: the rows are in the same order
   * either way.
   */
  const showOrderColumn = canUpdate;

  /*
   * ── The columns ──────────────────────────────────────────────────────────
   *
   * ⚠️ **The DECLARED key list is conditional, and that is what removes the two
   * CONTROL columns cleanly.** `useColumnLayout` treats the live key list as the
   * authority, so a key absent from it is dropped from the stored order and from
   * the manager — a reader who may not write is never offered an Order or an
   * Actions row to show. Storing the HIDDEN keys is what makes that safe in the
   * other direction: a column this table gains later appears for everybody
   * instead of defaulting to hidden.
   */
  const declaredColumnKeys = useMemo(
    () =>
      COLUMN_KEYS.filter(
        (key) => (key !== 'order' || showOrderColumn) && (key !== 'actions' || actionCount > 0),
      ),
    [showOrderColumn, actionCount],
  );
  const columns = useMemo(() => columnsFor(actionCount), [actionCount]);
  const columnLayout = useColumnLayout(
    'projects',
    declaredColumnKeys,
    LOCKED_COLUMN_KEYS,
    PINNED_COLUMN_KEYS,
  );

  /*
   * ⚠️ *SUPERSEDED: a local `frozenSideFor` that answered `'right'` for
   * `actions`, so the offsets counted the pinned lane.* `useFrozenOffsets` takes
   * `pinnedKeys` itself now and does exactly that, so the wrapper is gone and
   * the controller's own `frozenSideOf` is what everything reads.
   *
   * **The reason it existed is unchanged and is now the hook's**: a column the
   * READER freezes right is offset by the summed width of the frozen columns
   * outside it, and with the actions lane missing from that sum it would be
   * handed offset 0 and painted straight over Edit and Delete. Nothing errors;
   * the buttons simply stop being clickable, which is the failure that got this
   * column pinned in the first place.
   */
  const frozenSideOf = columnLayout.frozenSideOf;

  /*
   * The cast is safe rather than convenient: `applyOrder` filters the stored
   * order against the live keys, so nothing the hook returns can be a key this
   * table does not declare.
   */
  const visibleColumnKeys = useMemo(
    () => pinEnds(columnLayout.visibleKeys as readonly ProjectColumnKey[]),
    [columnLayout.visibleKeys],
  );

  /*
   * The header row, held by a ref on `<TR>` itself.
   *
   * ⚠️ *SUPERSEDED: "`TR` cannot take a ref … A callback ref on the wrapper
   * fires after its subtree is committed, so the row is already there to
   * find."* **`TR` takes a `ref` as of 2026-09-12**, so the spacer `<div>` and
   * its `querySelector('thead > tr')` are both gone — a selector reaching into
   * another component's markup is a coupling nothing would keep in step.
   *
   * The offsets still have to be MEASURED: this table has no `table-fixed`, so
   * a `w-*` on a `<th>` is a request the content outranks and the rendered
   * width is knowable only from the DOM.
   */
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    visibleColumnKeys,
    frozenSideOf,
    PINNED_COLUMN_KEYS,
  );

  /** The table's min-width floor, following the columns actually on screen. */
  const tableMinWidth = tableFloorPx(columns, visibleColumnKeys);

  /**
   * The manager's own view of the layout: Order first, Actions last, and every
   * move clamped into the band between them.
   *
   * ⚠️ **`lockedKeys` ALONE DOES NOT HOLD THE ENDS.** It stops those two being
   * moved themselves and says nothing about another column being dropped at
   * index 0 and pushing Order inboard — which would put the only drag grip in
   * the middle of the row it drags. Clamping on the way IN is what keeps the
   * stored order canonical, so the hook's index arithmetic and this list can
   * never disagree; `pinEnds` is then only a guard against a preferences
   * document written by something else.
   *
   * ⚠️ *SUPERSEDED: a local `setFrozen` refusing Actions, and a `frozenSideOf`
   * reporting it as right-frozen so the control drew as pressed.* **Both are
   * `pinnedKeys` now** — the hook refuses `setFrozen` on a pinned key and the
   * manager omits its two freeze controls outright, which is the better answer:
   * that edge is not the reader's to choose, so there is no state a pressed
   * button would be describing.
   *
   * ⛔ **`PINNED_COLUMN_KEYS` IS NOT `LOCKED_COLUMN_KEYS` HERE.** `order` is
   * locked and stays freezable; only `actions` is pinned.
   */
  const managerLayout = useMemo<ColumnLayoutController>(() => {
    const ordered = pinEnds(columnLayout.orderedKeys as readonly ProjectColumnKey[]);
    const lo = ordered[0] === 'order' ? 1 : 0;
    const hi = ordered[ordered.length - 1] === 'actions' ? ordered.length - 2 : ordered.length - 1;
    const clamp = (index: number) => Math.max(lo, Math.min(index, hi));
    return {
      ...columnLayout,
      orderedKeys: ordered,
      visibleKeys: visibleColumnKeys,
      moveTo: (key, index) => columnLayout.moveTo(key, clamp(index)),
      move: (key, direction) => {
        const index = ordered.indexOf(key as ProjectColumnKey);
        if (index === -1) return;
        columnLayout.moveTo(key, clamp(index + direction));
      },
    };
  }, [columnLayout, visibleColumnKeys]);

  const columnLabelOf = useCallback(
    (key: string) => columns[key as ProjectColumnKey]?.label ?? key,
    [columns],
  );
  const lockReasonOf = useCallback((key: string) => LOCK_REASONS[key], []);
  /** The Sort control's own words for the current view, so one copy says it. */
  const sortLabel =
    SORT_OPTIONS.find((option) => option.value === `${state.sort}:${state.order}`)?.label ??
    state.sort;

  function clearFilters() {
    update({ ...CLEARED_FILTERS });
  }

  function clearEverything() {
    setSearchInput('');
    update({ search: '', ...CLEARED_FILTERS });
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(project: Project) {
    setEditing(project);
    setFormOpen(true);
  }

  function requestDelete(project: Project) {
    setDeleteError(null);
    setDeleteBlocked(false);
    setPendingDelete(project);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    setDeleteBlocked(false);
    try {
      await api.deleteProject(pendingDelete.id);
      setPendingDelete(null);
      // Removing the only row on page 2+ would strand the user on an empty
      // final page; the page change refetches on its own.
      if (rows.length === 1 && state.page > 1) update({ page: state.page - 1 });
      else refresh();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the outcome the user wanted, so treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && state.page > 1) update({ page: state.page - 1 });
        else refresh();
      } else if (caught instanceof ApiError && caught.status === 403) {
        /*
         * A 403 body carries NO `details`, unlike a 422, so this can only ever
         * be a banner — never a field error. The control is hidden for anyone
         * below the admin tier, but a role can change under a live access token
         * (5.28 days here), so hiding it is an optimisation and this is the
         * guarantee.
         */
        setDeleteError(
          denialReason(ability, 'delete', PROJECTS) ??
            'Your account no longer has permission to delete projects.',
        );
      } else if (caught instanceof ApiError && caught.code === 'CONFLICT') {
        /*
         * The project is still named by a quote, invoice or deal LINE —
         * migration 067 made those `project_id` columns `ON DELETE RESTRICT`
         * (headers stayed `SET NULL`; see `DeleteDescription` below for the
         * full split).
         *
         * Switched on `code`, never on wording: the service emits TWO
         * different sentences under this one code (a counted one, and an
         * uncounted fallback when a line is inserted between the count and
         * the DELETE) — matching either string would miss the other. Shown
         * VERBATIM because the counts are exactly what this client cannot
         * derive.
         *
         * A 409 body carries no `details` (like a 403, unlike a 422), so this
         * is always a banner, never a per-field error.
         */
        setDeleteBlocked(true);
        setDeleteError(caught.message);
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  const countLabel = initialLoad
    ? 'Loading…'
    : `${pluralise(total, 'project')}${
        state.search ? ` matching “${state.search}”` : ''
      }${filterCount > 0 ? ` · ${pluralise(filterCount, 'filter')} applied` : ''}`;

  const newProjectButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={openCreate}>
      New project
    </Button>
  ) : null;

  const emptyState = (
    <ProjectsEmptyState
      hasError={Boolean(error)}
      narrowed={narrowed}
      onClear={clearEverything}
      canCreate={canCreate}
      onCreate={openCreate}
    />
  );

  /**
   * One row's cell, by column key — the body half of `COLUMN_KEYS`.
   *
   * ⚠️ **The `<TD>` itself is drawn by the caller, from the same descriptor the
   * header reads**, so a column's width cap cannot be left behind when the
   * reader moves the column. Only the CONTENT is chosen here.
   */
  const cellFor = (key: ProjectColumnKey, row: Project, index: number) => {
    switch (key) {
      case 'order':
        return (
          <ProjectOrderControls
            name={row.name}
            disabledReason={orderDisabledReason}
            moving={order.movingId === row.id}
            busy={order.movingId !== null}
            canMoveUp={index > 0}
            canMoveDown={index < rows.length - 1}
            onMoveUp={() => order.moveBy(row.id, -1)}
            onMoveDown={() => order.moveBy(row.id, 1)}
          />
        );
      case 'name':
        return (
          <div className="min-w-0">
            <ProjectNameCell row={row} edit={editContext} />
            <div className="mt-0.5">
              <ProjectDescriptionCell row={row} edit={editContext} />
            </div>
          </div>
        );
      case 'products':
        return <ProjectProductCell row={row} edit={editContext} />;
      /*
       * The period is two columns' worth of meaning in one cell, and they are
       * two separate writes: the cycle carries its intake with it, the intake
       * alone does not. Stacked rather than split so the pair reads as one
       * fact, exactly as `PeriodCell` did.
       */
      case 'period':
        return (
          <div className="grid min-w-0 gap-1">
            <ProjectCycleCell row={row} edit={editContext} />
            <ProjectIntakeCell row={row} edit={editContext} />
          </div>
        );
      /*
       * ⚠️ A SEPARATE column from Period: the cycle is the commercial period a
       * project is SOLD in; these are the calendar days it RUNS on. The two are
       * deliberately not constrained against each other server-side — a project
       * may run outside its cycle's window with no 422 — so folding them into
       * one cell would present two independent answers as one. Both dates are
       * independently nullable, so an empty half is an ordinary record, not a
       * gap.
       *
       * ⚠️ **Each bound carries a VISIBLE label**, the same two words the card
       * list uses, in the same `<dt>`/`<dd>` shape. The header names the pair
       * and cannot say which half is which; `InlineField`'s `label` is
       * `sr-only`, so it names the control for a screen reader and NOTHING for
       * a sighted reader; and every project carries `null` on both columns
       * today. Stacked unlabelled, that renders as two identical "Not set"
       * triggers under one heading — the state the owner reported (2026-08-30),
       * and the ONLY state currently reachable in production, so position can
       * never disambiguate them. A label naming a field is not helper text; it
       * is the label the sr-only string was standing in for.
       */
      case 'dates':
        return (
          <dl className="grid min-w-0 gap-1">
            <div className="flex items-baseline gap-2">
              <dt className="w-11 shrink-0 text-xs text-fg-subtle">Starts</dt>
              <dd className="m-0 min-w-0 flex-1">
                <ProjectStartDateCell row={row} edit={editContext} />
              </dd>
            </div>
            <div className="flex items-baseline gap-2">
              <dt className="w-11 shrink-0 text-xs text-fg-subtle">Ends</dt>
              <dd className="m-0 min-w-0 flex-1">
                <ProjectEndDateCell row={row} edit={editContext} />
              </dd>
            </div>
          </dl>
        );
      case 'institutions':
        return <ProjectCategoryCapacityCell row={row} edit={editContext} category="institution" />;
      case 'partners':
        return <ProjectCategoryCapacityCell row={row} edit={editContext} category="partner" />;
      case 'capacity':
        return <ProjectTotalCapacityCell row={row} />;
      case 'regions':
        return <ProjectRegionCell row={row} edit={editContext} />;
      case 'cities':
        return <ProjectCitiesCell row={row} edit={editContext} />;
      case 'status':
        return <ProjectStatusCell row={row} edit={editContext} />;
      case 'updated':
        return formatDateTime(row.updatedAt);
      case 'actions':
        return (
          <div className="flex items-center justify-end gap-1">
            <Tooltip label="Prices">
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                leadingIcon={<PriceTagIcon />}
                aria-label={`Prices for ${row.name}`}
                onClick={() => setPricingProject(row)}
              />
            </Tooltip>
            {canUpdate && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                leadingIcon={<PencilIcon />}
                aria-label={`Edit ${row.name}`}
                title={`Edit ${row.name}`}
                onClick={() => openEdit(row)}
              />
            )}
            {/* Delete is admin-only across the API; a 403 is still handled. */}
            {canDelete && (
              <Button
                variant="danger"
                size="sm"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${row.name}`}
                title={`Delete ${row.name}`}
                onClick={() => requestDelete(row)}
              />
            )}
          </div>
        );
    }
  };

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Records" title="Projects" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Records" title="Projects" actions={newProjectButton} />

      <Panel>
        <PanelHeader title="All projects" description={countLabel} />

        <div className="border-b border-border px-4 py-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Search by name">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search by name…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={searchInput}
                  maxLength={MAX_SEARCH}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              )}
            </Field>

            <FilterField
              label="Cycle"
              value={state.cycleId}
              // Changing the cycle clears the intake: an intake belongs to
              // exactly one cycle, so the pair could only ever return nothing.
              // The intake control is right beside this one and visibly returns
              // to "All intakes", so the change is not hidden.
              onChange={(value) => update({ cycleId: value, intakeId: '' })}
              options={withSelected(cycleOptions, state.cycleId, rowLabels.cycleId, 'Cycle')}
              allLabel="All cycles"
              loading={cycles.loading}
              // A dead `/cycles` read otherwise reaches this control as an
              // empty, perfectly innocent "All cycles".
              failed={cycles.failed}
              searchPlaceholder="Search cycles…"
            />

            <FilterField
              label="Product"
              value={state.productId}
              onChange={(value) => update({ productId: value })}
              options={withSelected(
                products.options,
                state.productId,
                rowLabels.productId,
                'Product',
              )}
              allLabel="All products"
              loading={products.loading}
              failed={products.failed}
              footnote={truncationNote(products)}
              searchPlaceholder="Search products…"
            />

            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                aria-expanded={showMoreFilters}
                aria-controls="project-more-filters"
                onClick={() => setShowMoreFilters((open) => !open)}
                className="w-full sm:w-auto"
              >
                {showMoreFilters ? 'Fewer filters' : 'More filters'}
              </Button>
              {/*
                ⚠️ Hidden below `lg`, where the rows are CARDS and there are no
                columns to manage — an affordance whose only outcome is nothing.

                ⚠️ The `hidden` sits on a WRAPPER, not on the trigger's own
                `className`: `Button` bakes in `inline-flex`, and two utilities
                setting `display` resolve by the emitted stylesheet's order
                rather than by the order they are written in, so it would be one
                Tailwind reshuffle away from showing at every width.
              */}
              <span className="hidden items-end lg:flex">
                <ColumnManager
                  layout={managerLayout}
                  labelOf={columnLabelOf}
                  lockedKeys={LOCKED_COLUMN_KEYS}
                  lockReasonOf={lockReasonOf}
                  pinnedKeys={PINNED_COLUMN_KEYS}
                />
              </span>
            </div>
          </div>

          {showMoreFilters && (
            <div
              id="project-more-filters"
              className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              <FilterField
                label="Intake"
                value={state.intakeId}
                onChange={(value) => update({ intakeId: value })}
                options={withSelected(
                  intakes.options,
                  state.intakeId,
                  rowLabels.intakeId,
                  'Intake',
                )}
                allLabel="All intakes"
                loading={intakes.loading}
                failed={intakes.failed}
                footnote={truncationNote(intakes)}
                searchPlaceholder="Search intakes…"
              />

              <FilterField
                label="City"
                value={state.cityId}
                onChange={(value) => update({ cityId: value })}
                options={withSelected(cities.options, state.cityId, rowLabels.cityId, 'City')}
                allLabel="All cities"
                loading={cities.loading}
                failed={cities.failed}
                footnote={truncationNote(cities)}
                searchPlaceholder="Search cities…"
              />

              {/*
                * The project's OWN regions of focus, not a city's and not a
                * country's. Only the active regions are offered, because a
                * retired one cannot be saved — the trade-off is spelled out
                * where `useReferenceOptions` is called above.
                *
                * The FILTER key is still the singular `regionId` (the WRITE key
                * is `regionIds`, migration 095). Since 2026-10-28 it takes a
                * comma-separated list: projects whose set includes ANY of them.
                * Do not rename it to plural to match the field beside it.
                */}
              <FilterField
                label="Region of focus"
                value={state.regionId}
                onChange={(value) => update({ regionId: value })}
                options={withSelected(regionOptions, state.regionId, rowLabels.regionId, 'Region')}
                allLabel="All regions"
                loading={regions.loading}
                failed={regions.failed}
                searchPlaceholder="Search regions…"
              />

              <Field label="Status">
                {({ id }) => (
                  <Select
                    id={id}
                    value={state.isActive}
                    onChange={(e) =>
                      update({ isActive: e.target.value as ProjectFilters['isActive'] })
                    }
                  >
                    <option value="">Active and retired</option>
                    <option value="true">Active only</option>
                    <option value="false">Retired only</option>
                  </Select>
                )}
              </Field>

              {/*
                * The four period bounds, as two ranges. They are separate
                * questions and not one four-ended control: a project's start
                * and its end are independently nullable, so "starts in Q1" and
                * "ends in Q1" select different sets and a project can match
                * either, both or neither.
                *
                * Every bound is INCLUSIVE (`>=` / `<=`), so a range names the
                * two days at its ends rather than the gap between them.
                */}
              <PeriodRangeFilter
                legend="Starts between"
                from={state.startDateAfter}
                to={state.startDateBefore}
                onFrom={(value) => update({ startDateAfter: dateBound(value) })}
                onTo={(value) => update({ startDateBefore: dateBound(value) })}
              />

              <PeriodRangeFilter
                legend="Ends between"
                from={state.endDateAfter}
                to={state.endDateBefore}
                onFrom={(value) => update({ endDateAfter: dateBound(value) })}
                onTo={(value) => update({ endDateBefore: dateBound(value) })}
              />

              {/*
                * Said ONCE for all four bounds rather than under each range —
                * it is one fact about the columns, and repeating it four times
                * would read as four different warnings. See
                * `PERIOD_FILTER_NOTE` for why it is stated at all and why it
                * carries no count.
                */}
              <p className="m-0 text-2xs text-fg-subtle sm:col-span-2 lg:col-span-4">
                {PERIOD_FILTER_NOTE}
              </p>

              {/*
                * One sort control at every width, rather than clickable headers
                * on wide screens and a select on narrow ones. Two mechanisms for
                * one query param is how they end up disagreeing — and below `lg`
                * the rows are cards with no headers to click at all. The sorted
                * column is still marked with `aria-sort` on its header.
                */}
              <Field label="Sort">
                {({ id }) => (
                  <Select
                    id={id}
                    value={`${state.sort}:${state.order}`}
                    onChange={(e) => {
                      const [nextSort, nextOrder] = e.target.value.split(':') as [
                        ProjectSort,
                        ListOrder,
                      ];
                      update({ sort: nextSort, order: nextOrder });
                    }}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {/*
                * Shown only while a date sort is chosen, because it is only
                * true of those two. `DATE_SORT_NOTE` carries the reasoning: it
                * reports WHERE the undated rows went, which is otherwise
                * invisible and reads as a broken sort.
                */}
              {DATE_SORT_KEYS.includes(state.sort) && (
                <p className="m-0 self-end text-2xs text-fg-subtle">{DATE_SORT_NOTE}</p>
              )}
            </div>
          )}

          {filterCount > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <p className="m-0 text-xs text-fg-muted">
                {pluralise(filterCount, 'filter')} applied
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {/*
          ⚠️ **A failed move is already rolled back by the time this renders** —
          the rows on screen are what the server holds. It is a banner and never
          a field error: a 403 and the two anchor 422s reach different keys, and
          nothing on this row is bound to `beforeId`.
        */}
        {order.moveError && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title={<>Couldn&rsquo;t move that project.</>}
            action={
              <Button variant="ghost" size="sm" onClick={order.dismissMoveError}>
                Dismiss
              </Button>
            }
          >
            <span className="[overflow-wrap:anywhere]">{order.moveError}</span>
          </Notice>
        )}

        {/*
          Two FACTS about what can be rearranged, not advice — the exception the
          no-helper-text rule keeps for "why is this control disabled" and for
          "this list is not all of it".

          The second is the one that costs something to leave out: a page of 100
          out of 137 looks exactly like a complete list, and a reader who orders
          it end to end has ordered a hundred of them. `perPage` maxes at 100 and
          101 is a 422, so a cycle bigger than that genuinely cannot be ordered
          in one view; the same sentence covers the ordinary paged case, where a
          project cannot be moved onto a page it cannot see.
        */}
        {showOrderColumn && rows.length > 0 && (!manualOrder || total > rows.length) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-4 py-2 text-2xs text-fg-subtle">
            {manualOrder ? (
              <span>
                Showing {rows.length} of {total} — only these can be reordered.
              </span>
            ) : (
              <>
                <span>Sorted by {sortLabel}, so the rows cannot be rearranged.</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => update({ sort: MANUAL_SORT, order: 'asc' })}
                >
                  Use manual order
                </Button>
              </>
            )}
          </div>
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title={<>Couldn&rsquo;t load projects.</>}
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below lg a table this wide cannot fit 320px, so rows become cards. */}
          <div className="lg:hidden">
            {initialLoad ? (
              <ListSkeleton />
            ) : rows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {rows.map((row, index) => (
                  <li
                    key={row.id}
                    className={cx(
                      'border-b border-border px-4 py-3 last:border-b-0',
                      order.movingId === row.id && 'opacity-60',
                    )}
                  >
                    {/*
                      * The same cells as the table, in a <dt>/<dd> list rather
                      * than a row. `InlineField` is built for this shape — the
                      * trigger names its own field, because a <dt> is not a
                      * <label> — so the two views cannot disagree about what a
                      * column may hold or how a refusal reads.
                      */}
                    <div className="min-w-0">
                      <ProjectNameCell row={row} edit={editContext} />
                      <div className="mt-0.5">
                        <ProjectDescriptionCell row={row} edit={editContext} />
                      </div>
                    </div>

                    <dl className="mt-2.5 grid gap-1.5 text-xs">
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Status</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectStatusCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Products</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectProductCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Cycle</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectCycleCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Intake</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectIntakeCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      {/*
                        * Two rows here, one stacked cell in the table — and
                        * since 2026-08-30 BOTH name each bound, in these same
                        * two words. The "Dates" header can only name the pair,
                        * and the two dates are independently nullable, so
                        * "Ends 12 Aug 2026" with nothing against "Starts" is a
                        * legitimate record and not a half-rendered one — which
                        * is exactly why neither view may rely on position to
                        * say which half is which.
                        */}
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Starts</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectStartDateCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Ends</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectEndDateCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Institutions</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectCategoryCapacityCell row={row} edit={editContext} category="institution" />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Partners</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectCategoryCapacityCell row={row} edit={editContext} category="partner" />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Total capacity</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectTotalCapacityCell row={row} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Regions</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectRegionCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Cities</dt>
                        <dd className="m-0 min-w-0 flex-1">
                          <ProjectCitiesCell row={row} edit={editContext} />
                        </dd>
                      </div>
                      {/* Server-maintained, and deliberately not editable. */}
                      <div className="flex items-baseline gap-2">
                        <dt className="w-20 shrink-0 text-fg-subtle">Updated</dt>
                        <dd className="m-0 min-w-0 flex-1 text-fg-muted tabular">
                          {formatDateTime(row.updatedAt)}
                        </dd>
                      </div>
                    </dl>

                    {actionCount > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {/*
                          ⚠️ THE TOUCH PATH, and the only one there is here.
                          Native HTML5 drag does not work on touch at all — the
                          kanban boards had to grow a per-card Move menu for
                          exactly this — so these two buttons are not a
                          fallback for a drag, they ARE how a project is
                          reordered at these widths. They are also the keyboard
                          path, which is why building them once covered both.

                          No grip: this list is a `<ul>` of cards, not a drop
                          surface, and a handle that cannot be dragged
                          advertises a path that does nothing.
                        */}
                        {showOrderColumn && (
                          <ProjectOrderControls
                            name={row.name}
                            disabledReason={orderDisabledReason}
                            moving={order.movingId === row.id}
                            busy={order.movingId !== null}
                            canMoveUp={index > 0}
                            canMoveDown={index < rows.length - 1}
                            onMoveUp={() => order.moveBy(row.id, -1)}
                            onMoveDown={() => order.moveBy(row.id, 1)}
                            showHandle={false}
                          />
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<PriceTagIcon />}
                          aria-label={`Prices for ${row.name}`}
                          onClick={() => setPricingProject(row)}
                        >
                          Prices
                        </Button>
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => requestDelete(row)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden lg:block">
            {/*
              ⚠️ **ONE table across the loading state and the loaded one**, where
              there used to be two branches each building their own. The
              `<Table>` and the header ROW are then the same DOM nodes
              throughout — which is what keeps the MEASURED freeze offsets, and
              the ResizeObserver behind them, attached across the
              skeleton → rows swap. Two branches remount that row, and the
              offsets would go on describing an element already thrown away:
              nothing errors, the frozen columns just overlap by the difference.
              **Do not split this back into two branches.**
            */}
            {!initialLoad && rows.length === 0 ? (
              emptyState
            ) : (
              <TableWrap frozenHeader>
                {/*
                  ⚠️ The min-width FLOOR goes through `style`, never a class.
                  Tailwind reads class names out of the source text, so a
                  computed `min-w-[…px]` generates no rule at all and the table
                  quietly loses its floor — the columns then compress instead of
                  the wrapper scrolling.

                  ⚠️ *SUPERSEDED: "`Table` is a shared primitive and takes no
                  `style`, so the floor is carried by this wrapper."* **`Table`
                  takes a `style` now**, so the spacer `<div>` that existed only
                  to hold this number is gone.
                */}
                <Table style={{ minWidth: `${tableMinWidth}px` }}>
                  <ProjectsTableHead
                    rowRef={headRowRef}
                    sort={state.sort}
                    order={state.order}
                    columns={columns}
                    visibleKeys={visibleColumnKeys}
                    frozenSideOf={frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                  {initialLoad ? (
                    <TableSkeleton
                      columns={columns}
                      visibleKeys={visibleColumnKeys}
                      actionCount={actionCount}
                      frozenSideOf={frozenSideOf}
                      frozenOffsets={frozenOffsets}
                    />
                  ) : (
                    <TBody>
                      {rows.map((row, index) => (
                        <TR
                          key={row.id}
                          interactive
                          /*
                           * Draggable ONLY for a press that started in the grip —
                           * see `armedRowId`. `undefined` rather than `false` so
                           * the attribute is absent, which is what the drag spec
                           * treats as "inherit", and what every other row is.
                           */
                          {...(canReorder && armedRowId === row.id ? { draggable: true } : {})}
                          /*
                           * Capture phase, so it runs before any inline editor
                           * inside the row can stop the event. It ARMS on a press
                           * inside the grip and disarms on every other press, so
                           * one handler covers both directions.
                           */
                          onPointerDownCapture={
                            canReorder
                              ? (event) => {
                                  const target = event.target;
                                  setArmedRowId(
                                    target instanceof Element &&
                                      target.closest(PROJECT_DRAG_HANDLE_SELECTOR) !== null
                                      ? row.id
                                      : null,
                                  );
                                }
                              : undefined
                          }
                          onPointerUp={canReorder ? () => setArmedRowId(null) : undefined}
                          onPointerCancel={canReorder ? () => setArmedRowId(null) : undefined}
                          onDragStart={
                            canReorder
                              ? (event: ReactDragEvent<HTMLTableRowElement>) => {
                                  event.dataTransfer.effectAllowed = 'move';
                                  event.dataTransfer.setData(PROJECT_DRAG_MIME, String(row.id));
                                  event.dataTransfer.setData('text/plain', row.name);
                                  setDraggingId(row.id);
                                }
                              : undefined
                          }
                          onDragEnd={canReorder ? endDrag : undefined}
                          onDragOver={
                            canReorder
                              ? (event: ReactDragEvent<HTMLTableRowElement>) => {
                                  /*
                                   * A row is not a target for itself. The drop
                                   * would express nothing, and refusing it here
                                   * makes the browser show a "no drop" cursor
                                   * rather than accepting a gesture that then
                                   * does nothing — the honest answer, and the
                                   * same rule as a same-column kanban drop.
                                   */
                                  if (draggingId === null || draggingId === row.id) return;
                                  event.preventDefault();
                                  event.dataTransfer.dropEffect = 'move';
                                  setDropTargetId(row.id);
                                }
                              : undefined
                          }
                          onDragLeave={
                            canReorder
                              ? () =>
                                  setDropTargetId((current) =>
                                    current === row.id ? null : current,
                                  )
                              : undefined
                          }
                          onDrop={
                            canReorder
                              ? (event: ReactDragEvent<HTMLTableRowElement>) => {
                                  event.preventDefault();
                                  const dragged = event.dataTransfer.getData(PROJECT_DRAG_MIME);
                                  endDrag();
                                  /*
                                   * The id arrives as a STRING. `moveOnto`
                                   * normalises both sides before it compares
                                   * them — `'4' === 4` is false, and an
                                   * unnormalised guard would let a same-position
                                   * drop through to a request that expresses
                                   * nothing.
                                   */
                                  if (dragged !== '') order.moveOnto(dragged, row.id);
                                }
                              : undefined
                          }
                          /*
                           * ⚠️ The drop marker is an OUTLINE as well as a tint.
                           * `TR interactive` carries `hover:bg-surface-hover`,
                           * and two utilities setting one property resolve by
                           * stylesheet order rather than by the order they are
                           * written here — so a tint alone is one Tailwind
                           * reshuffle away from being invisible exactly when it
                           * matters. `outline` collides with nothing.
                           */
                          className={cx(
                            draggingId === row.id && 'opacity-50',
                            order.movingId === row.id && 'opacity-60',
                            dropTargetId === row.id &&
                              'bg-accent-brand-tint outline-2 -outline-offset-2 outline-accent-brand',
                          )}
                        >
                          {visibleColumnKeys.map((key) => (
                            <TD
                              key={key}
                              className={columns[key].td}
                              numeric={key === 'actions'}
                              pinned={key === 'actions'}
                              frozen={key === 'actions' ? undefined : frozenSideOf(key)}
                              frozenOffset={key === 'actions' ? undefined : frozenOffsets[key]}
                            >
                              {cellFor(key, row, index)}
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {/* ⚠️ Capacity is recorded and NOT enforced — no counter, no
          * remaining-places field, no 409 — a deal, quote, invoice or
          * deliverable that exceeds it is accepted in full, by design. The
          * sentence that said so was removed on the owner's stricter
          * no-helper-text ruling (2026-09-05). */}
        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No projects to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <div className="w-36">
                <Field label="Projects per page" hideLabel>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={String(state.perPage)}
                      onChange={(e) => update({ perPage: Number(e.target.value) })}
                    >
                      {PER_PAGE_CHOICES.map((choice) => (
                        <option key={choice} value={choice}>
                          {choice} per page
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {state.page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={state.page <= 1}
                onClick={() => update({ page: state.page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={state.page >= totalPages}
                onClick={() => update({ page: state.page + 1 })}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <ProjectFormModal
        open={formOpen}
        project={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          refresh();
        }}
      />

      <ProjectPricingDialog project={pricingProject} onClose={() => setPricingProject(null)} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
          setDeleteBlocked(false);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete project?'}
        description={<DeleteDescription project={pendingDelete} />}
        confirmLabel="Delete project"
        loading={deleting}
        {...(deleteError
          ? {
              error: (
                <>
                  {/*
                    The server's sentence, unaltered — it names the line-item
                    counts and nothing on this side knows them.
                  */}
                  <span className="block [overflow-wrap:anywhere]">{deleteError}</span>
                  {deleteBlocked && (
                    <span className="mt-1.5 block">
                      Remove this project from those line items, or set it to Retired instead.
                    </span>
                  )}
                </>
              ),
            }
          : {})}
      />
    </>
  );
}

/**
 * Plain headers plus `aria-sort` on the sorted one — the Sort select in the
 * filter bank is the one sort control at every width, including the card list
 * below `lg` with no headers to click.
 *
 * ⚠️ **`aria-sort` is carried by the COLUMN, not by a position.** It is set
 * only where `PROJECT_COLUMNS` names `sortKeys`, which is only where the API can
 * really order: `sort=capacity` is a 422 on a `.strict()` query rather than a
 * parameter the server ignores, and an `aria-sort="none"` on such a heading
 * promises a control that will never exist. Attaching it positionally — as this
 * did, to the second, fifth and tenth `<TH>` — survives exactly until somebody
 * moves a column, and then reads out the wrong heading as the sorted one with
 * nothing failing.
 *
 * ⚠️ **Dates is ONE header over TWO sortable keys**, which is why `sortKeys` is
 * a list: `startDate` and `endDate` share a column, and marking only one would
 * leave it reading as unsorted half the time it is sorted.
 *
 * ⚠️ **The row is rendered from `visibleKeys` and NOTHING ELSE**, so it cannot
 * fall out of step with the body or the skeleton. The header cells are also what
 * `useFrozenOffsets` measures, in this order — the hook indexes the row's
 * children positionally, so the array passed here must be the array the body
 * maps over. `rowRef` is how it reaches the row to measure.
 */
function ProjectsTableHead({
  rowRef,
  sort,
  order,
  columns,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
}: {
  /**
   * The row `useFrozenOffsets` measures.
   *
   * ⚠️ *SUPERSEDED: the page found this row with a callback ref on a wrapper
   * `<div>` and a `querySelector('thead > tr')`, because `TR` took no ref.*
   * **It takes one now**, so the row is handed over rather than hunted for —
   * a selector reaching into this component's markup from the page was a
   * coupling that would break silently if the row ever gained a wrapper.
   */
  rowRef: RefObject<HTMLTableRowElement | null>;
  sort: ProjectSort;
  order: ListOrder;
  columns: Record<ProjectColumnKey, ProjectColumn>;
  visibleKeys: readonly ProjectColumnKey[];
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
}) {
  const ariaSort = (keys: readonly ProjectSort[]) =>
    keys.includes(sort) ? (order === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <THead>
      <TR ref={rowRef}>
        {visibleKeys.map((key) => {
          const column = columns[key];
          return (
            <TH
              key={key}
              className={column.th}
              numeric={key === 'actions'}
              pinned={key === 'actions'}
              /*
               * ⚠️ No `frozen` on the pinned lane — see the same guard in
               * `TableSkeleton`.
               */
              frozen={key === 'actions' ? undefined : frozenSideOf(key)}
              frozenOffset={key === 'actions' ? undefined : frozenOffsets[key]}
              /*
               * `undefined` rather than `'none'` where the column is not
               * sortable, so the attribute is ABSENT — see the header note.
               */
              aria-sort={column.sortKeys ? ariaSort(column.sortKeys) : undefined}
            >
              {column.label}
            </TH>
          );
        })}
      </TR>
    </THead>
  );
}

/**
 * What deleting a project actually does — splits two ways, and stating only
 * one of them is how someone is surprised.
 *
 * ⚠️ Migration 067 put `project_id` on the three LINE-ITEM tables as
 * `ON DELETE RESTRICT`, so a project any quote, invoice or deal **line** still
 * names is refused with a 409 naming the counts.
 *
 *  - A **header** that loses its project is degraded but still meaningful — a
 *    deal that lost its project is still a real deal with real money on it.
 *    All four header columns stayed `ON DELETE SET NULL`, so those never block.
 *  - A **line** that lost its project is not meaningful: its cities were
 *    validated against that project, so the line would be left naming cities
 *    nothing justifies. Those `RESTRICT`.
 *
 * `f_project_cities` cascades either way: "project 12 runs in Bangalore" is
 * not a fact about anything once project 12 is gone.
 *
 * **This dialog counts neither side, and says so.** The header counts would be
 * four extra requests per open against a limiter shared across the office
 * (⚠️ *SUPERSEDED: "a 120-per-60s limiter."* It is **12,000/60s**, corrected
 * 2026-09-17), and no endpoint counts line items by project at all — the server
 * names those numbers only in the refusal itself, which is why that message
 * is surfaced verbatim.
 */
function DeleteDescription({ project }: { project: Project | null }) {
  if (!project) return null;
  const cities = project.cities.length;
  return (
    <>
      <strong>
        Refused if any quote, invoice or deal line still names “{project.name}”.
      </strong>{' '}
      Deals, quotes, invoices and deliverables are kept and lose this project
      {cities > 0 ? ` and its ${pluralise(cities, 'city', 'cities')}` : ''}.{' '}
      {project.isActive ? 'Retiring it instead is never refused. ' : ''}
      This cannot be undone.
    </>
  );
}

function ProjectsEmptyState({
  hasError,
  narrowed,
  onClear,
  canCreate,
  onCreate,
}: {
  hasError: boolean;
  narrowed: boolean;
  onClear: () => void;
  canCreate: boolean;
  onCreate: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<ProjectsIcon />}
        title="Nothing to show"
        description="See the message above."
      />
    );
  }

  if (narrowed) {
    return (
      <EmptyState
        icon={<ProjectsIcon />}
        title="No projects match"
        description="Nothing matches this search or filter."
        action={
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search and filters
          </Button>
        }
      />
    );
  }

  // The permanent, ordinary empty state — see file header. Nothing here
  // implies data is on its way.
  return (
    <EmptyState
      icon={<ProjectsIcon />}
      title="No projects yet"
      action={
        canCreate ? (
          <Button variant="secondary" size="sm" leadingIcon={<PlusIcon />} onClick={onCreate}>
            New project
          </Button>
        ) : null
      }
    />
  );
}
